import React, { useState, useMemo, useEffect } from "react";
import {
  Calendar, Users, DollarSign, ClipboardList, BarChart3,
  Plus, X, Trash2, Edit2, Phone, MapPin, CheckCircle2,
  TrendingUp, TrendingDown, Wallet, CreditCard, Banknote,
  Play, Ban, Filter, UserPlus, ChevronLeft, ChevronRight, ChevronDown, CalendarDays, Repeat, StickyNote, Contact, ClipboardCheck, Navigation, LogOut, FileText, Printer, Settings, Mail
} from "lucide-react";
import { fetchAuthSession } from "aws-amplify/auth";
import { loadAll, seedAll, db, emailInvoice } from "./data";

// ---------- Seed data ----------
const TEAM_COLORS = ["#2F8F6B", "#C9694F", "#5B7C99", "#8A6D9F", "#A68A4F"];

const initialTeams = [
  { id: "t1", name: "Team Alpha", members: ["Maria", "Joel"], color: TEAM_COLORS[0] },
  { id: "t2", name: "Team Bravo", members: ["Sandra", "Luis"], color: TEAM_COLORS[1] },
  { id: "t3", name: "Team Coastal", members: ["Anita", "Mike", "Beth"], color: TEAM_COLORS[2] },
];

const initialMembers = [
  { id: "m1", name: "Maria", phone: "(702) 555-0301", address: "1180 Desert Willow Dr, Las Vegas, NV", hireDate: "2024-03-10", notes: "" },
  { id: "m2", name: "Joel", phone: "(702) 555-0312", address: "742 Bonanza Rd, Las Vegas, NV", hireDate: "2024-05-22", notes: "" },
  { id: "m3", name: "Sandra", phone: "(702) 555-0327", address: "915 Paradise Rd, Las Vegas, NV", hireDate: "2023-11-04", notes: "Prefers morning shifts." },
  { id: "m4", name: "Luis", phone: "(702) 555-0339", address: "204 Eastern Ave, Henderson, NV", hireDate: "2025-01-15", notes: "" },
  { id: "m5", name: "Anita", phone: "(702) 555-0344", address: "560 Sahara Ave, Las Vegas, NV", hireDate: "2022-08-19", notes: "" },
  { id: "m6", name: "Mike", phone: "(702) 555-0358", address: "33 Flamingo Rd, Las Vegas, NV", hireDate: "2024-09-02", notes: "" },
  { id: "m7", name: "Beth", phone: "(702) 555-0361", address: "812 Charleston Blvd, Las Vegas, NV", hireDate: "2025-02-28", notes: "Has a valid driver's license, can drive the van." },
];

const initialQuoteVisits = [
  { id: "q1", customerId: "c1", member: "Maria", date: "2026-06-19", time: "10:00", status: "scheduled", notes: "3-bedroom house, wants biweekly service." },
  { id: "q2", customerId: "c2", member: "Sandra", date: "2026-06-20", time: "14:00", status: "completed", notes: "Quoted $120 standard clean. Client interested." },
];


const initialCustomers = [
  { id: "c1", name: "Diane Forrest", phone: "(702) 555-0142", address: "412 Sage Ct, Las Vegas, NV", notes: "Has two dogs, leave gate latched.", clientStatus: "returning" },
  { id: "c2", name: "Marcus Webb", phone: "(702) 555-0198", address: "88 Desert Bloom Way, Henderson, NV", notes: "Prefers eco-friendly products only.", clientStatus: "returning" },
  { id: "c3", name: "Priya Anand", phone: "(702) 555-0177", address: "215 Cactus Flat Rd, Las Vegas, NV", notes: "Code for gate: 4471.", clientStatus: "new" },
  { id: "c4", name: "Carol Langenfeld", phone: "+(843)2678298", address: "908 Adaline Ct, Myrtle Beach, SC", notes: "Client requires use of her own products and cleaning done her way. First clean: $250, following biweekly cleans: $150.", clientStatus: "new" },
  { id: "c5", name: "Booe Realty", phone: "+1(843)4494477", address: "7728 North Kings Hwy, Myrtle Beach, SC 29572", notes: "Contact: Wendy Harrison (Housekeeping Manager) — Tel: +1(843)8778509 — Email: wendy@booerealty.net", clientStatus: "new" },
];

const initialJobs = [
  { id: "j1", customerId: "c1", teamId: "t1", date: "2026-06-16", time: "09:00", service: "Standard clean", status: "scheduled", notes: "", assignments: [{ member: "Maria", amount: 40 }, { member: "Joel", amount: 40 }], recurrence: { frequency: "weekly" }, seriesId: "s1" },
  { id: "j2", customerId: "c2", teamId: "t2", date: "2026-06-16", time: "11:30", service: "Deep clean", status: "scheduled", notes: "", assignments: [{ member: "Sandra", amount: 55 }, { member: "Luis", amount: 55 }], recurrence: { frequency: "none" }, seriesId: null },
  { id: "j3", customerId: "c3", teamId: "t3", date: "2026-06-17", time: "08:30", service: "Move-out clean", status: "scheduled", notes: "", assignments: [{ member: "Anita", amount: 50 }, { member: "Mike", amount: 50 }, { member: "Beth", amount: 50 }], recurrence: { frequency: "none" }, seriesId: null },
  { id: "j4", customerId: "c1", teamId: "t1", date: "2026-06-09", time: "09:00", service: "Standard clean", status: "completed", notes: "Went smoothly.", assignments: [{ member: "Maria", amount: 40 }, { member: "Joel", amount: 40 }], recurrence: { frequency: "none" }, seriesId: null },
  { id: "j5", customerId: "c2", teamId: "t2", date: "2026-06-10", time: "13:00", service: "Standard clean", status: "completed", notes: "", assignments: [{ member: "Sandra", amount: 45 }], recurrence: { frequency: "none" }, seriesId: null },
];

const initialPayments = [
  { id: "p1", customerId: "c1", jobId: "j4", date: "2026-06-09", amount: 120, method: "Card", notes: "" },
  { id: "p2", customerId: "c2", jobId: "j5", date: "2026-06-10", amount: 95, method: "Cash", notes: "Paid in full at door" },
];

const initialExpenses = [
  { id: "e1", date: "2026-06-08", category: "Supplies", description: "Cleaning solutions & restock", amount: 86.4 },
  { id: "e2", date: "2026-06-11", category: "Fuel", description: "Team Bravo van gas", amount: 52.0 },
  { id: "e3", date: "2026-06-12", category: "Payroll", description: "Weekly wages - Team Alpha", amount: 640.0 },
];

const QUOTE_STATUS_STYLES = {
  scheduled: { bg: "#E4F0FA", color: "#1E6FA8", label: "Scheduled" },
  completed: { bg: "#DDEDE9", color: "#1A6B5A", label: "Visited" },
  converted: { bg: "#E8F5E2", color: "#3A7D44", label: "Converted to job" },
  cancelled: { bg: "#F4E3DD", color: "#C9694F", label: "Cancelled" },
};


const STATUS_STYLES = {
  scheduled: { bg: "#E4F0FA", color: "#1E6FA8", label: "Scheduled" },
  "in-progress": { bg: "#FBF0DD", color: "#A6772E", label: "In progress" },
  completed: { bg: "#DDEDE9", color: "#1A6B5A", label: "Completed" },
  cancelled: { bg: "#F4E3DD", color: "#C9694F", label: "Cancelled" },
};

const PAYMENT_METHODS = ["Cash", "Card", "Check", "Bank Transfer", "Online"];
const SERVICE_TYPES = ["Standard clean", "Deep clean", "Move-out clean", "Move-in clean", "Post-construction", "One-time special"];
const EXPENSE_CATEGORIES = ["Supplies", "Fuel", "Payroll", "Equipment", "Insurance", "Marketing", "Other"];

// Everything with a name reads A–Z by default. Case- and accent-insensitive, and numeric-aware
// so "Apt 2" sorts before "Apt 10" — a plain localeCompare puts 10 first.
const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
const alphabetical = (a, b) => collator.compare(a || "", b || "");
const byName = (a, b) => alphabetical(a?.name, b?.name);
const RECURRENCE_OPTIONS = [
  { value: "none", label: "Does not repeat" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
];
const RECURRENCE_HORIZON_MONTHS = 6; // how far ahead occurrences stay materialized; rolls forward automatically as time passes

function addInterval(dateStr, frequency) {
  const d = new Date(dateStr + "T00:00:00");
  if (frequency === "weekly") d.setDate(d.getDate() + 7);
  else if (frequency === "biweekly") d.setDate(d.getDate() + 14);
  else if (frequency === "monthly") d.setMonth(d.getMonth() + 1);
  // Read back the local calendar components (the date was built at local
  // midnight) rather than toISOString(), which would convert to UTC and can
  // land on the previous day.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function uid(prefix) {
  return prefix + Math.random().toString(36).slice(2, 9);
}

// Returns YYYY-MM-DD for the given moment (default: now) in US Eastern time.
// Using toISOString() would give the UTC date, which rolls over to "tomorrow"
// during US evenings — so all "today" math must go through here instead.
function easternDateStr(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(date);
}

// Business identity shown on printed invoices. Editable from the Settings tab,
// persisted in localStorage since there's no backend field for it yet.
const BUSINESS_INFO_KEY = "cs-business-info";
const DEFAULT_BUSINESS_INFO = {
  name: "Fabi's Spic & Span",
  tagline: "Cleaning Services",
  address: "PO Box 51643. Myrtle Beach, SC. 29579",
  phone: "+1(843)331-8404",
  email: "afspicandspan@gmail.com",
};
function loadBusinessInfo() {
  try {
    const stored = JSON.parse(localStorage.getItem(BUSINESS_INFO_KEY));
    return { ...DEFAULT_BUSINESS_INFO, ...stored };
  } catch {
    return DEFAULT_BUSINESS_INFO;
  }
}

// A job has no client-facing price field of its own; the sum of the crew payouts
// is the only money figure on it, so we use it as the suggested line amount.
function jobAmount(j) {
  return (j.assignments || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
}

// Next sequential invoice number (INV-0001, INV-0002, ...) derived from existing ones.
function nextInvoiceNumber(invoices) {
  const max = invoices.reduce((m, inv) => {
    const n = parseInt(String(inv.invoiceNumber || "").replace(/\D/g, ""), 10);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return "INV-" + String(max + 1).padStart(4, "0");
}

const INVOICE_STATUS_STYLES = {
  unpaid: { bg: "#FBF0DD", color: "#A6772E", label: "Unpaid" },
  paid: { bg: "#DDEDE9", color: "#1A6B5A", label: "Paid" },
};

function fmtMoney(n) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtDate(d) {
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

// ---------- Reusable bits ----------
// Team picker as a real dropdown. It's a native <select>, so it keeps keyboard support and the
// platform's own picker on mobile; the browser arrow is turned off and redrawn with the app's
// chevron. The colour lives in the dot beside it rather than on the options, since per-option
// styling isn't honoured consistently across browsers.
// `dot` colours a swatch on the left (used by the team picker); omit it and the control just
// carries its label. `activeWhen` decides when the control shows as "filtering", which for a
// filter is any value other than "all" but for a sort is never.
function SelectFilter({ value, onChange, options, label, dot, activeWhen, width }) {
  const isActive = activeWhen !== undefined ? activeWhen : value !== "all";
  return (
    <span style={styles.selectWrap}>
      {dot !== undefined && <span style={{ ...styles.teamDot, ...styles.selectDot, background: dot || "#B7C2BD" }} />}
      <select
        style={{
          ...styles.selectInput,
          ...(dot === undefined ? styles.selectInputNoDot : {}),
          ...(isActive ? styles.selectInputActive : {}),
          ...(width ? { minWidth: width } : {}),
        }}
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-label={label}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown size={14} style={styles.selectChevron} />
    </span>
  );
}

function TeamSelect({ teams, value, onChange, allLabel = "All teams", extraOptions = [] }) {
  const selected = teams.find(t => t.id === value);
  return (
    <SelectFilter
      label="Filter by team"
      value={value}
      onChange={onChange}
      dot={selected?.color || "#B7C2BD"}
      options={[
        { value: "all", label: allLabel },
        ...teams.map(t => ({ value: t.id, label: t.name })),
        ...extraOptions,
      ]}
    />
  );
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modalCard, maxWidth: wide ? 640 : 460 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{title}</h3>
          <button onClick={onClose} style={styles.iconBtn}><X size={18} /></button>
        </div>
        <div style={{ padding: "20px 24px 24px" }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function Pill({ bg, color, children }) {
  return (
    <span style={{ ...styles.pill, background: bg, color }}>{children}</span>
  );
}

function StatCard({ icon, label, value, accent, sub }) {
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statIconWrap, background: accent + "1A", color: accent }}>{icon}</div>
      <div>
        <div style={styles.statLabel}>{label}</div>
        <div style={styles.statValue}>{value}</div>
        {sub && <div style={styles.statSub}>{sub}</div>}
      </div>
    </div>
  );
}

// ---------- Main App ----------
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.matchMedia("(max-width: 760px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 760px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export default function App({ signOut, user }) {
  const isMobile = useIsMobile();

  // Role from Cognito group membership. Anyone not in "admin" is a read-only viewer.
  const [role, setRole] = useState(null);
  useEffect(() => {
    fetchAuthSession()
      .then((session) => {
        const groups = session.tokens?.idToken?.payload?.["cognito:groups"];
        const isAdmin = Array.isArray(groups) && groups.includes("admin");
        setRole(isAdmin ? "admin" : "viewer");
      })
      .catch(() => setRole("viewer"));
  }, []);
  const isAdmin = role === "admin";
  const perms = { canEdit: isAdmin, showMoney: isAdmin, showPhone: isAdmin };

  const [tab, setTab] = useState("schedule");
  // Core models are loaded from the Amplify backend (see ./data). They start empty
  // and are filled by the mount effect below (seeding from the initial* data on first run).
  // Held unsorted as they come back from the backend, then exposed A–Z below so every tab,
  // table, and dropdown that consumes them is alphabetical without each one re-sorting.
  const [teamsRaw, setTeams] = useState([]);
  const [membersRaw, setMembers] = useState([]);
  const [customersRaw, setCustomers] = useState([]);
  const teams = useMemo(() => [...teamsRaw].sort(byName), [teamsRaw]);
  const members = useMemo(() => [...membersRaw].sort(byName), [membersRaw]);
  const customers = useMemo(() => [...customersRaw].sort(byName), [customersRaw]);
  const [jobs, setJobs] = useState([]);
  const [expenses, setExpenses] = useState([]);
  // payments + quoteVisits + invoices are backend-persisted (loaded below).
  const [payments, setPayments] = useState([]);
  const [quoteVisits, setQuoteVisits] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Load everything from the backend once after login. Only seed when the database is
  // brand-new (every collection empty) — a true first run. We must NOT seed just because
  // one collection happens to be empty, or deleting the last item in any section would
  // resurrect the demo records on the next load.
  // The ref guard prevents StrictMode's double-invoke from racing two concurrent seeds.
  const didInit = React.useRef(false);
  useEffect(() => {
    if (didInit.current) return; // the ref guard alone ensures this runs exactly once
    didInit.current = true;
    (async () => {
      try {
        let data = await loadAll();
        // Demo seeding is OFF for this live database. Auto-populating sample records
        // whenever a collection looked empty kept resurrecting demo jobs/visits/payments
        // after the user deliberately cleared them. The seed machinery below is retained
        // only for bootstrapping a brand-new throwaway instance — flip this to true there.
        const ENABLE_DEMO_SEED = false;
        const needsSeed = ENABLE_DEMO_SEED &&
          data.customers.length === 0 && data.jobs.length === 0 &&
          data.teams.length === 0 && data.members.length === 0 &&
          data.expenses.length === 0 && data.quoteVisits.length === 0 &&
          data.payments.length === 0;
        if (needsSeed) {
          data = await seedAll({
            customers: initialCustomers,
            teams: initialTeams,
            members: initialMembers,
            jobs: initialJobs,
            expenses: initialExpenses,
            quoteVisits: initialQuoteVisits,
            payments: initialPayments,
          }, data);
        }
        setCustomers(data.customers);
        setJobs(data.jobs);
        setTeams(data.teams);
        setMembers(data.members);
        setExpenses(data.expenses);
        setQuoteVisits(data.quoteVisits);
        setPayments(data.payments);
        setInvoices(data.invoices);
        setLoadError(null);
      } catch (err) {
        setLoadError(err.message || String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const [jobModal, setJobModal] = useState(null); // job being edited or "new"
  const [teamModal, setTeamModal] = useState(null);
  const [memberModal, setMemberModal] = useState(null);
  const [customerModal, setCustomerModal] = useState(null);
  const [quoteModal, setQuoteModal] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [invoicePrint, setInvoicePrint] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(loadBusinessInfo);

  function saveBusinessInfo(info) {
    localStorage.setItem(BUSINESS_INFO_KEY, JSON.stringify(info));
    setBusinessInfo(info);
  }
  const [paymentModal, setPaymentModal] = useState(null);
  const [expenseModal, setExpenseModal] = useState(null);
  const [teamFilter, setTeamFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);
  const [historyCustomer, setHistoryCustomer] = useState(null);

  const customerMap = useMemo(() => Object.fromEntries(customers.map(c => [c.id, c])), [customers]);
  const teamMap = useMemo(() => Object.fromEntries(teams.map(t => [t.id, t])), [teams]);

  // Keep recurring job series materialized up to a rolling horizon, so the calendar always has
  // upcoming occurrences ready without needing to generate an unbounded number up front.
  useEffect(() => {
    if (loading) return; // don't materialize until the backend data has loaded

    const horizonDate = new Date();
    horizonDate.setMonth(horizonDate.getMonth() + RECURRENCE_HORIZON_MONTHS);
    const horizonStr = easternDateStr(horizonDate);

    const seriesIds = [...new Set(jobs.filter(j => j.seriesId && j.recurrence?.frequency !== "none").map(j => j.seriesId))];
    const additions = [];

    seriesIds.forEach(seriesId => {
      const occurrences = jobs.filter(j => j.seriesId === seriesId);
      const latest = occurrences.reduce((max, j) => (j.date > max.date ? j : max), occurrences[0]);
      if (!latest || latest.recurrence?.frequency === "none") return;

      let cursorDate = latest.date;
      let cursorJob = latest;
      while (cursorDate < horizonStr) {
        const nextDate = addInterval(cursorDate, cursorJob.recurrence.frequency);
        const nextJob = {
          ...cursorJob,
          id: uid("j"),
          date: nextDate,
          status: "scheduled",
        };
        additions.push(nextJob);
        cursorDate = nextDate;
        cursorJob = nextJob;
      }
    });

    if (additions.length > 0) {
      setJobs(prev => [...prev, ...additions]);
      // Persist the newly materialized occurrences. Once stored, subsequent loads
      // already include them so this converges and won't re-create duplicates.
      additions.forEach(j => { db.job.create(j).catch(err => console.error("persist occurrence failed", err)); });
    }
  }, [jobs, loading]);

  // The schedule stays chronological — a calendar read out of time order is unusable — but
  // jobs sharing a slot are listed alphabetically by client.
  const sortedJobs = useMemo(() => {
    return [...jobs]
      .filter(j => teamFilter === "all" || j.teamId === teamFilter)
      .filter(j => statusFilter === "all" || j.status === statusFilter)
      .sort((a, b) =>
        (a.date + a.time).localeCompare(b.date + b.time) ||
        alphabetical(customerMap[a.customerId]?.name, customerMap[b.customerId]?.name)
      );
  }, [jobs, teamFilter, statusFilter, customerMap]);

  // Surfaces a backend write failure without blocking the optimistic UI update.
  function reportWrite(promise, what) {
    Promise.resolve(promise).catch(err => {
      console.error(`${what} failed`, err);
      setLoadError(`Could not save ${what} to the server. Your change is shown locally but may not have been saved. ${err.message || ""}`);
    });
  }

  function saveJob(job, scope) {
    // New job (no id yet): just create it. If it has a recurrence, give it a seriesId so future
    // occurrences can be materialized and linked together.
    if (!job.id) {
      const seriesId = job.recurrence?.frequency !== "none" ? uid("s") : null;
      const created = { ...job, id: uid("j"), seriesId };
      setJobs(prev => [...prev, created]);
      reportWrite(db.job.create(created), "job");
      setJobModal(null);
      return;
    }

    // Editing an existing job that isn't part of a series, or explicitly scoped to "this" occurrence only.
    if (!job.seriesId || scope === "this") {
      const updated = { ...job, seriesId: scope === "this" ? null : job.seriesId };
      setJobs(prev => prev.map(j => j.id === job.id ? updated : j));
      reportWrite(db.job.update(updated), "job");
      setJobModal(null);
      return;
    }

    // Scope is "future": apply the change to this occurrence and every later occurrence in the series,
    // and remove any already-materialized future ones so they regenerate from the new settings.
    const futureToRemove = jobs.filter(j => j.seriesId === job.seriesId && j.date > job.date);
    setJobs(prev => {
      const withoutFuture = prev.filter(j => !(j.seriesId === job.seriesId && j.date > job.date));
      return withoutFuture.map(j => j.id === job.id ? job : j);
    });
    reportWrite(db.job.update(job), "job");
    futureToRemove.forEach(j => reportWrite(db.job.remove(j.id), "job"));
    setJobModal(null);
  }

  function deleteJob(id, scope) {
    const target = jobs.find(j => j.id === id);
    if (!target || !target.seriesId || scope === "this") {
      setJobs(prev => prev.filter(j => j.id !== id));
      reportWrite(db.job.remove(id), "job");
    } else {
      // Delete this and all future occurrences in the series.
      const toRemove = jobs.filter(j => j.seriesId === target.seriesId && j.date >= target.date);
      setJobs(prev => prev.filter(j => !(j.seriesId === target.seriesId && j.date >= target.date)));
      toRemove.forEach(j => reportWrite(db.job.remove(j.id), "job"));
    }
    setJobModal(null);
  }
  function saveTeam(team) {
    if (team.id) {
      setTeams(prev => prev.map(t => t.id === team.id ? team : t));
      reportWrite(db.team.update(team), "team");
    } else {
      const created = { ...team, id: uid("t") };
      setTeams(prev => [...prev, created]);
      reportWrite(db.team.create(created), "team");
    }
    setTeamModal(null);
  }
  function deleteTeam(id) {
    setTeams(prev => prev.filter(t => t.id !== id));
    reportWrite(db.team.remove(id), "team");
    setTeamModal(null);
  }
  function saveMember(member) {
    if (member.id) {
      const previous = members.find(m => m.id === member.id);
      // If the name changed, propagate it into team rosters and job assignments so nothing goes stale.
      if (previous && previous.name !== member.name) {
        const updatedTeams = teams
          .map(t => ({ ...t, members: t.members.map(name => name === previous.name ? member.name : name) }))
          .filter((t, i) => JSON.stringify(t.members) !== JSON.stringify(teams[i].members));
        const updatedJobs = jobs
          .map(j => ({ ...j, assignments: (j.assignments || []).map(a => a.member === previous.name ? { ...a, member: member.name } : a) }))
          .filter((j, i) => JSON.stringify(j.assignments) !== JSON.stringify(jobs[i].assignments));

        setTeams(prev => prev.map(t => ({
          ...t,
          members: t.members.map(name => name === previous.name ? member.name : name),
        })));
        setJobs(prev => prev.map(j => ({
          ...j,
          assignments: (j.assignments || []).map(a => a.member === previous.name ? { ...a, member: member.name } : a),
        })));
        updatedTeams.forEach(t => reportWrite(db.team.update(t), "team"));
        updatedJobs.forEach(j => reportWrite(db.job.update(j), "job"));
      }
      setMembers(prev => prev.map(m => m.id === member.id ? member : m));
      reportWrite(db.member.update(member), "member");
    } else {
      const created = { ...member, id: uid("m") };
      setMembers(prev => [...prev, created]);
      reportWrite(db.member.create(created), "member");
    }
    setMemberModal(null);
  }
  function deleteMember(id) {
    setMembers(prev => prev.filter(m => m.id !== id));
    reportWrite(db.member.remove(id), "member");
    setMemberModal(null);
  }
  function saveCustomer(cust) {
    if (cust.id) {
      setCustomers(prev => prev.map(c => c.id === cust.id ? cust : c));
      reportWrite(db.customer.update(cust), "client");
    } else {
      const created = { ...cust, id: uid("c") };
      setCustomers(prev => [...prev, created]);
      reportWrite(db.customer.create(created), "client");
    }
    setCustomerModal(null);
  }
  function deleteCustomer(id) {
    setCustomers(prev => prev.filter(c => c.id !== id));
    reportWrite(db.customer.remove(id), "client");
    setCustomerModal(null);
  }
  function saveQuoteVisit(visit) {
    if (visit.id) {
      setQuoteVisits(prev => prev.map(v => v.id === visit.id ? visit : v));
      reportWrite(db.quoteVisit.update(visit), "quick visit");
    } else {
      const created = { ...visit, id: uid("q") };
      setQuoteVisits(prev => [...prev, created]);
      reportWrite(db.quoteVisit.create(created), "quick visit");
    }
    setQuoteModal(null);
  }
  function deleteQuoteVisit(id) {
    setQuoteVisits(prev => prev.filter(v => v.id !== id));
    reportWrite(db.quoteVisit.remove(id), "quick visit");
    setQuoteModal(null);
  }
  function savePayment(pay) {
    if (pay.id) {
      setPayments(prev => prev.map(p => p.id === pay.id ? pay : p));
      reportWrite(db.payment.update(pay), "payment");
    } else {
      const created = { ...pay, id: uid("p") };
      setPayments(prev => [...prev, created]);
      reportWrite(db.payment.create(created), "payment");
    }
    setPaymentModal(null);
  }
  function deletePayment(id) {
    setPayments(prev => prev.filter(p => p.id !== id));
    reportWrite(db.payment.remove(id), "payment");
    setPaymentModal(null);
  }
  function saveInvoice(inv) {
    if (inv.id) {
      setInvoices(prev => prev.map(i => i.id === inv.id ? inv : i));
      reportWrite(db.invoice.update(inv), "invoice");
      setInvoiceModal(null);
      return inv;
    }
    const created = { ...inv, id: uid("inv"), invoiceNumber: inv.invoiceNumber || nextInvoiceNumber(invoices) };
    setInvoices(prev => [...prev, created]);
    reportWrite(db.invoice.create(created), "invoice");
    setInvoiceModal(null);
    return created;
  }
  function deleteInvoice(id) {
    setInvoices(prev => prev.filter(i => i.id !== id));
    reportWrite(db.invoice.remove(id), "invoice");
    setInvoiceModal(null);
  }
  function saveExpense(exp) {
    if (exp.id) {
      setExpenses(prev => prev.map(e => e.id === exp.id ? exp : e));
      reportWrite(db.expense.update(exp), "expense");
    } else {
      const created = { ...exp, id: uid("e") };
      setExpenses(prev => [...prev, created]);
      reportWrite(db.expense.create(created), "expense");
    }
    setExpenseModal(null);
  }
  function deleteExpense(id) {
    setExpenses(prev => prev.filter(e => e.id !== id));
    reportWrite(db.expense.remove(id), "expense");
    setExpenseModal(null);
  }

  const NAV = [
    { key: "schedule", label: "Schedule", icon: <Calendar size={17} /> },
    { key: "quotes", label: "Quick Visits", icon: <ClipboardCheck size={17} /> },
    { key: "teams", label: "Teams", icon: <Users size={17} /> },
    { key: "members", label: "Members", icon: <Contact size={17} /> },
    { key: "customers", label: "Clients", icon: <ClipboardList size={17} /> },
    { key: "payments", label: "Payments", icon: <DollarSign size={17} /> },
    { key: "invoices", label: "Invoices", icon: <FileText size={17} /> },
    { key: "reports", label: "Reports", icon: <BarChart3 size={17} /> },
    { key: "settings", label: "Settings", icon: <Settings size={17} /> },
  ];

  // Viewers (non-admins) can only access the Schedule tab.
  const visibleNav = isAdmin ? NAV : NAV.filter(n => n.key === "schedule");
  // If a viewer is somehow on a restricted tab, send them back to Schedule.
  useEffect(() => {
    if (role && !isAdmin && !visibleNav.some(n => n.key === tab)) {
      setTab("schedule");
    }
  }, [role, isAdmin, tab, visibleNav]);

  if (loading) {
    return (
      <div style={{ ...styles.app, alignItems: "center", justifyContent: "center" }}>
        <style>{globalCss}</style>
        <div style={{ color: "#5B7C99", fontSize: 15 }}>Loading your data…</div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.app, ...(isMobile ? styles.appMobile : {}) }}>
      <style>{globalCss}</style>

      {loadError && (
        <div style={styles.errorBanner}>
          {loadError}
          <button style={styles.errorDismiss} onClick={() => setLoadError(null)}>✕</button>
        </div>
      )}

      {/* Sidebar */}
      <div style={{ ...styles.sidebar, ...(isMobile ? styles.sidebarMobile : {}) }}>
        <div style={styles.brand}>
          <div style={styles.brandMark}>FS</div>
          <div>
            <div style={styles.brandName}>Fabi's Spic &amp; Span</div>
            <div style={styles.brandSub}>Operations</div>
          </div>
        </div>

        <nav style={isMobile ? styles.navMobile : { marginTop: 28 }}>
          {visibleNav.map(n => (
            <button
              key={n.key}
              onClick={() => setTab(n.key)}
              style={{ ...styles.navBtn, ...(isMobile ? styles.navBtnMobile : {}), ...(tab === n.key ? styles.navBtnActive : {}) }}
            >
              {n.icon}<span>{n.label}</span>
            </button>
          ))}
        </nav>

        {!isMobile && <div style={styles.sidebarFooter}>
          <div style={styles.teamLegendTitle}>Teams on route</div>
          {teams.map(t => (
            <div key={t.id} style={styles.teamLegendRow}>
              <span style={{ ...styles.teamDot, background: t.color }} />
              <span style={styles.teamLegendName}>{t.name}</span>
            </div>
          ))}
        </div>}
      </div>

      {/* Main content */}
      <div style={{ ...styles.main, ...(isMobile ? styles.mainMobile : {}) }}>
        <div style={{ ...styles.topBar, ...(isMobile ? styles.topBarMobile : {}) }}>
          <span style={styles.topBarEmail}>
            {user?.signInDetails?.loginId || user?.username || "Signed in"}
          </span>
          <button style={styles.topBarSignOut} onClick={signOut}>
            <LogOut size={15} /> <span>Log out</span>
          </button>
        </div>
        {tab === "schedule" && (
          <ScheduleView
            jobs={sortedJobs}
            customerMap={customerMap}
            teamMap={teamMap}
            teams={teams}
            teamFilter={teamFilter}
            setTeamFilter={setTeamFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            onNew={() => setJobModal({ id: null, customerId: customers[0]?.id || "", teamId: teams[0]?.id || "", date: selectedDate || easternDateStr(), time: "09:00", service: SERVICE_TYPES[0], status: "scheduled", notes: "", assignments: [], recurrence: { frequency: "none" }, seriesId: null })}
            onEdit={(j) => setJobModal(j)}
            onStatusChange={(j, status) => saveJob({ ...j, status })}
            perms={perms}
          />
        )}

        {tab === "quotes" && (
          <QuoteVisitsView
            quoteVisits={quoteVisits}
            customers={customers}
            members={members}
            customerMap={customerMap}
            onNew={() => setQuoteModal({ id: null, customerId: customers[0]?.id || "", member: members[0]?.name || "", date: easternDateStr(), time: "10:00", status: "scheduled", notes: "" })}
            onEdit={(v) => setQuoteModal(v)}
            onStatusChange={(v, status) => saveQuoteVisit({ ...v, status })}
          />
        )}

        {tab === "teams" && (
          <TeamsView
            teams={teams}
            jobs={jobs}
            onNew={() => setTeamModal({ id: null, name: "", members: [], color: TEAM_COLORS[teams.length % TEAM_COLORS.length] })}
            onEdit={(t) => setTeamModal(t)}
          />
        )}

        {tab === "members" && (
          <MembersView
            members={members}
            teams={teams}
            jobs={jobs}
            onNew={() => setMemberModal({ id: null, name: "", phone: "", address: "", hireDate: easternDateStr(), notes: "" })}
            onEdit={(m) => setMemberModal(m)}
          />
        )}

        {tab === "customers" && (
          <CustomersView
            customers={customers}
            jobs={jobs}
            payments={payments}
            teamMap={teamMap}
            onNew={() => setCustomerModal({ id: null, name: "", phone: "", email: "", address: "", notes: "", clientStatus: "new" })}
            onEdit={(c) => setCustomerModal(c)}
            onHistory={(c) => setHistoryCustomer(c)}
          />
        )}

        {tab === "payments" && (
          <PaymentsView
            payments={payments}
            customerMap={customerMap}
            customers={customers}
            jobs={jobs}
            teams={teams}
            onNew={() => setPaymentModal({ id: null, customerId: customers[0]?.id || "", jobId: "", date: easternDateStr(), amount: "", method: "Card", notes: "" })}
            onEdit={(p) => setPaymentModal(p)}
          />
        )}

        {tab === "invoices" && (
          <InvoicesView
            invoices={invoices}
            customerMap={customerMap}
            onNew={() => setInvoiceModal({ id: null, invoiceNumber: "", customerId: customers[0]?.id || "", issueDate: easternDateStr(), periodStart: "", periodEnd: "", lineItems: [], notes: "", total: 0, status: "unpaid" })}
            onEdit={(inv) => setInvoiceModal(inv)}
            onPrint={(inv) => setInvoicePrint(inv)}
          />
        )}

        {tab === "reports" && (
          <ReportsView
            payments={payments}
            expenses={expenses}
            jobs={jobs}
            teams={teams}
            customers={customers}
            onNewExpense={() => setExpenseModal({ id: null, date: easternDateStr(), category: EXPENSE_CATEGORIES[0], description: "", amount: "" })}
            onEditExpense={(e) => setExpenseModal(e)}
          />
        )}

        {tab === "settings" && (
          <SettingsView businessInfo={businessInfo} onSave={saveBusinessInfo} />
        )}
      </div>

      {/* Modals */}
      {jobModal && (
        <JobModal job={jobModal} customers={customers} teams={teams} onSave={saveJob} onDelete={deleteJob} onClose={() => setJobModal(null)} />
      )}
      {teamModal && (
        <TeamModal team={teamModal} jobCount={jobs.filter(j => j.teamId === teamModal.id).length} onSave={saveTeam} onDelete={deleteTeam} onClose={() => setTeamModal(null)} />
      )}
      {memberModal && (
        <MemberModal member={memberModal} onSave={saveMember} onDelete={deleteMember} onClose={() => setMemberModal(null)} />
      )}
      {customerModal && (
        <CustomerModal customer={customerModal} onSave={saveCustomer} onDelete={deleteCustomer} onClose={() => setCustomerModal(null)} />
      )}
      {quoteModal && (
        <QuoteModal visit={quoteModal} customers={customers} members={members} onSave={saveQuoteVisit} onDelete={deleteQuoteVisit} onClose={() => setQuoteModal(null)} />
      )}
      {paymentModal && (
        <PaymentModal payment={paymentModal} customers={customers} jobs={jobs} onSave={savePayment} onDelete={deletePayment} onClose={() => setPaymentModal(null)} />
      )}
      {expenseModal && (
        <ExpenseModal expense={expenseModal} onSave={saveExpense} onDelete={deleteExpense} onClose={() => setExpenseModal(null)} />
      )}
      {invoiceModal && (
        <InvoiceModal
          invoice={invoiceModal}
          customers={customers}
          jobs={jobs}
          invoices={invoices}
          onSave={saveInvoice}
          onDelete={deleteInvoice}
          onClose={() => setInvoiceModal(null)}
          onSaveAndPrint={(inv) => { const saved = saveInvoice(inv); setInvoicePrint(saved); }}
        />
      )}
      {invoicePrint && (
        <InvoicePrintView
          invoice={invoicePrint}
          customer={customerMap[invoicePrint.customerId]}
          businessInfo={businessInfo}
          onClose={() => setInvoicePrint(null)}
        />
      )}
      {historyCustomer && (
        <HistoryModal
          customer={historyCustomer}
          jobs={jobs.filter(j => j.customerId === historyCustomer.id)}
          payments={payments.filter(p => p.customerId === historyCustomer.id)}
          teamMap={teamMap}
          onClose={() => setHistoryCustomer(null)}
        />
      )}
    </div>
  );
}

// ---------- Schedule ----------
function ScheduleView({ jobs, customerMap, teamMap, teams, teamFilter, setTeamFilter, statusFilter, setStatusFilter, selectedDate, setSelectedDate, onNew, onEdit, onStatusChange, perms = { canEdit: true, showMoney: true } }) {
  const [showCalendar, setShowCalendar] = useState(false);

  const visibleJobs = useMemo(() => {
    if (!selectedDate) return jobs;
    return jobs.filter(j => j.date === selectedDate);
  }, [jobs, selectedDate]);

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Schedule</h1>
          <p style={styles.pageSub}>Jobs grouped by day, color-coded by team.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            style={{ ...styles.secondaryBtnSm, ...(showCalendar ? styles.secondaryBtnSmActive : {}) }}
            onClick={() => setShowCalendar(v => !v)}
          >
            <CalendarDays size={15} /> {showCalendar ? "Hide calendar" : "Pick a day"}
          </button>
          {perms.canEdit && <button style={styles.primaryBtn} onClick={onNew}><Plus size={16} /> New job</button>}
        </div>
      </div>

      {showCalendar && (
        <MiniCalendar items={jobs} getDotColor={(j) => teamMap[j.teamId]?.color} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
      )}

      {selectedDate && (
        <div style={styles.selectedDayBanner}>
          <CalendarDays size={15} />
          <span>Showing <strong>{dayLabel(selectedDate)}</strong>, {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
          <button style={styles.clearDayBtn} onClick={() => setSelectedDate(null)}><X size={13} /> Clear</button>
        </div>
      )}

      <div style={styles.filterRow}>
        <span style={styles.filterRowLabel}><Users size={13} /> Team</span>
        <TeamSelect teams={teams} value={teamFilter} onChange={setTeamFilter} />
      </div>

      <div style={styles.filterRow}>
        <span style={styles.filterRowLabel}><Filter size={13} /> Status</span>
        <SelectFilter
          label="Filter by status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: "all", label: "All statuses" },
            ...Object.keys(STATUS_STYLES).map(s => ({ value: s, label: STATUS_STYLES[s].label })),
          ]}
        />
      </div>

      <ScheduleByDay jobs={visibleJobs} customerMap={customerMap} teamMap={teamMap} onEdit={onEdit} onStatusChange={onStatusChange} perms={perms} />
    </div>
  );
}

function MiniCalendar({ items, getDotColor, selectedDate, onSelectDate }) {
  const todayStr = easternDateStr();
  const initialMonth = selectedDate ? new Date(selectedDate + "T00:00:00") : new Date(todayStr + "T00:00:00");
  const [viewMonth, setViewMonth] = useState(new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));

  const itemsByDate = useMemo(() => {
    const m = {};
    items.forEach(it => { (m[it.date] = m[it.date] || []).push(it); });
    return m;
  }, [items]);

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = viewMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  function dateKey(d) {
    return `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }

  function changeMonth(delta) {
    setViewMonth(new Date(year, month + delta, 1));
  }

  return (
    <div style={styles.calendarCard}>
      <div style={styles.calendarHeader}>
        <button style={styles.calendarNavBtn} onClick={() => changeMonth(-1)}><ChevronLeft size={16} /></button>
        <span style={styles.calendarMonthLabel}>{monthLabel}</span>
        <button style={styles.calendarNavBtn} onClick={() => changeMonth(1)}><ChevronRight size={16} /></button>
        <button style={styles.calendarTodayBtn} onClick={() => { setViewMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1)); onSelectDate(todayStr); }}>Today</button>
      </div>
      <div style={styles.calendarWeekRow}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <span key={i} style={styles.calendarWeekday}>{d}</span>)}
      </div>
      <div style={styles.calendarGrid}>
        {cells.map((d, i) => {
          if (d === null) return <span key={i} style={styles.calendarEmptyCell} />;
          const key = dateKey(d);
          const dayItems = itemsByDate[key] || [];
          const isToday = key === todayStr;
          const isSelected = key === selectedDate;
          const dotColors = [...new Set(dayItems.map(getDotColor).filter(Boolean))].slice(0, 3);
          return (
            <button
              key={i}
              onClick={() => onSelectDate(isSelected ? null : key)}
              style={{
                ...styles.calendarDayCell,
                ...(isToday ? styles.calendarDayCellToday : {}),
                ...(isSelected ? styles.calendarDayCellSelected : {}),
              }}
            >
              <span>{d}</span>
              {dotColors.length > 0 && (
                <span style={styles.calendarDotRow}>
                  {dotColors.map((c, ci) => <span key={ci} style={{ ...styles.calendarDot, background: isSelected ? "#fff" : c }} />)}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function dayLabel(dateStr) {
  const today = easternDateStr();
  const tomorrow = easternDateStr(new Date(Date.now() + 86400000));
  const yesterday = easternDateStr(new Date(Date.now() - 86400000));
  if (dateStr === today) return "Today";
  if (dateStr === tomorrow) return "Tomorrow";
  if (dateStr === yesterday) return "Yesterday";
  const dt = new Date(dateStr + "T00:00:00");
  return dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

function ScheduleByDay({ jobs, customerMap, teamMap, onEdit, onStatusChange, perms = { canEdit: true, showMoney: true } }) {
  const grouped = useMemo(() => {
    const m = {};
    jobs.forEach(j => { (m[j.date] = m[j.date] || []).push(j); });
    return Object.entries(m).sort((a, b) => a[0].localeCompare(b[0]));
  }, [jobs]);

  const today = easternDateStr();

  if (jobs.length === 0) {
    return <div style={styles.emptyState}>No jobs scheduled. Add one to get started.</div>;
  }

  return (
    <div style={styles.dayGroupList}>
      {grouped.map(([date, dayJobs]) => (
        <div key={date} style={styles.dayGroup}>
          <div style={styles.dayGroupHeader}>
            <span style={{ ...styles.dayGroupTitle, ...(date === today ? styles.dayGroupTitleToday : {}) }}>{dayLabel(date)}</span>
            <span style={styles.dayGroupSub}>{new Date(date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
            <span style={styles.dayGroupCount}>{dayJobs.length} job{dayJobs.length !== 1 ? "s" : ""}</span>
          </div>
          <div style={styles.jobList}>
            {dayJobs.map((j, idx) => {
              const cust = customerMap[j.customerId];
              const team = teamMap[j.teamId];
              const status = STATUS_STYLES[j.status];

              // Find the next job in the day for the same team, to offer driving directions between them.
              const nextJob = dayJobs.slice(idx + 1).find(nj => nj.teamId === j.teamId);
              const nextCust = nextJob ? customerMap[nextJob.customerId] : null;
              const showDriveLink = nextJob && cust?.address && nextCust?.address;

              return (
                <React.Fragment key={j.id}>
                <div style={styles.jobCard}>
                  <div style={{ ...styles.jobTeamBar, background: team?.color || "#ccc" }} />
                  <div style={styles.jobCardBody}>
                    <div style={styles.jobCardTop}>
                      <div>
                        <div style={styles.jobDate}>{j.time}</div>
                        <div style={styles.jobCustomer}>
                          {cust?.name || "Unknown client"}
                          {j.seriesId && <Repeat size={13} style={styles.jobRepeatIcon} />}
                        </div>
                      </div>
                      <Pill bg={status.bg} color={status.color}>{status.label}</Pill>
                    </div>
                    <div style={styles.jobMeta}>
                      <span><ClipboardList size={14} /> {j.service}</span>
                      <span><Users size={14} /> {team?.name || "Unassigned"}</span>
                      {cust?.address && <span><MapPin size={14} /> {cust.address}</span>}
                    </div>
                    {j.notes && (
                      <div style={styles.jobNoteRow}>
                        <StickyNote size={13} style={styles.jobNoteIcon} />
                        <span>{j.notes}</span>
                      </div>
                    )}
                    {j.assignments && j.assignments.length > 0 && (
                      <div style={styles.jobAssignRow}>
                        {j.assignments.map((a, ai) => (
                          <span key={ai} style={styles.jobAssignChip}>
                            {a.member}{perms.showMoney ? ` · ${fmtMoney(Number(a.amount) || 0)}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                    {perms.canEdit && (
                    <div style={styles.jobActions}>
                      {j.status === "scheduled" && (
                        <button style={styles.linkBtn} onClick={() => onStatusChange(j, "in-progress")}><Play size={14} /> Start job</button>
                      )}
                      {j.status === "in-progress" && (
                        <button style={styles.linkBtn} onClick={() => onStatusChange(j, "completed")}><CheckCircle2 size={14} /> Mark completed</button>
                      )}
                      {(j.status === "scheduled" || j.status === "in-progress") && (
                        <button style={{ ...styles.linkBtn, color: "#C9694F" }} onClick={() => onStatusChange(j, "cancelled")}><Ban size={14} /> Cancel</button>
                      )}
                      <button style={styles.linkBtn} onClick={() => onEdit(j)}><Edit2 size={14} /> Edit</button>
                    </div>
                    )}
                  </div>
                </div>
                {showDriveLink && (
                  <DriveConnector fromAddress={cust.address} toAddress={nextCust.address} teamColor={team?.color} />
                )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function DriveConnector({ fromAddress, toAddress, teamColor }) {
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(fromAddress)}&destination=${encodeURIComponent(toAddress)}&travelmode=driving`;
  return (
    <div style={styles.driveConnector}>
      <div style={{ ...styles.driveConnectorLine, background: teamColor || "#CFE2F0" }} />
      <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={styles.driveConnectorLink}>
        <Navigation size={13} /> Drive to next job
      </a>
      <div style={{ ...styles.driveConnectorLine, background: teamColor || "#CFE2F0" }} />
    </div>
  );
}

// ---------- Quick Visits ----------
function QuoteVisitsView({ quoteVisits, customers, members, customerMap, onNew, onEdit, onStatusChange }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const sorted = useMemo(() => {
    return [...quoteVisits]
      .filter(v => statusFilter === "all" || v.status === statusFilter)
      .filter(v => !selectedDate || v.date === selectedDate)
      // Chronological like the schedule, alphabetical by client within the same slot.
      .sort((a, b) =>
        (a.date + a.time).localeCompare(b.date + b.time) ||
        alphabetical(customerMap[a.customerId]?.name, customerMap[b.customerId]?.name)
      );
  }, [quoteVisits, statusFilter, selectedDate, customerMap]);

  const counts = useMemo(() => {
    const m = {};
    Object.keys(QUOTE_STATUS_STYLES).forEach(s => { m[s] = quoteVisits.filter(v => v.status === s).length; });
    return m;
  }, [quoteVisits]);

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Quick Visits</h1>
          <p style={styles.pageSub}>First visits to assess and quote new clients.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            style={{ ...styles.secondaryBtnSm, ...(showCalendar ? styles.secondaryBtnSmActive : {}) }}
            onClick={() => setShowCalendar(v => !v)}
          >
            <CalendarDays size={15} /> {showCalendar ? "Hide calendar" : "Pick a day"}
          </button>
          <button style={styles.primaryBtn} onClick={onNew}><Plus size={16} /> New visit</button>
        </div>
      </div>

      {showCalendar && (
        <MiniCalendar items={quoteVisits} getDotColor={(v) => QUOTE_STATUS_STYLES[v.status]?.color} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
      )}

      {selectedDate && (
        <div style={styles.selectedDayBanner}>
          <CalendarDays size={15} />
          <span>Showing <strong>{dayLabel(selectedDate)}</strong>, {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
          <button style={styles.clearDayBtn} onClick={() => setSelectedDate(null)}><X size={13} /> Clear</button>
        </div>
      )}

      <div style={styles.quoteStatsRow}>
        {Object.entries(QUOTE_STATUS_STYLES).map(([key, style]) => (
          <div key={key} style={{ ...styles.quoteStatCard, borderColor: style.color + "40", background: style.bg }}>
            <div style={{ ...styles.quoteStatNum, color: style.color }}>{counts[key] || 0}</div>
            <div style={{ ...styles.quoteStatLabel, color: style.color }}>{style.label}</div>
          </div>
        ))}
      </div>

      <div style={styles.filterRow}>
        <span style={styles.filterRowLabel}><Filter size={13} /> Status</span>
        <button onClick={() => setStatusFilter("all")} style={{ ...styles.filterChip, ...(statusFilter === "all" ? styles.filterChipActive : {}) }}>All</button>
        {Object.entries(QUOTE_STATUS_STYLES).map(([key, style]) => (
          <button key={key} onClick={() => setStatusFilter(key)}
            style={{ ...styles.filterChip, ...(statusFilter === key ? { background: style.color, color: "#fff", borderColor: style.color } : {}) }}>
            {style.label}
          </button>
        ))}
      </div>

      <div style={styles.jobList}>
        {sorted.length === 0 && <div style={styles.emptyState}>{quoteVisits.length === 0 ? "No quick visits yet. Add one to get started." : "No quick visits match these filters."}</div>}
        {sorted.map(v => {
          const cust = customerMap[v.customerId];
          const qstyle = QUOTE_STATUS_STYLES[v.status];
          return (
            <div key={v.id} style={styles.quoteCard}>
              <div style={{ ...styles.jobTeamBar, background: qstyle.color }} />
              <div style={styles.jobCardBody}>
                <div style={styles.jobCardTop}>
                  <div>
                    <div style={styles.jobDate}>{fmtDate(v.date)} · {v.time}</div>
                    <div style={styles.jobCustomer}>{cust?.name || "Unknown client"}</div>
                  </div>
                  <Pill bg={qstyle.bg} color={qstyle.color}>{qstyle.label}</Pill>
                </div>
                <div style={styles.jobMeta}>
                  {v.member && <span><Users size={14} /> {v.member}</span>}
                  {cust?.address && <span><MapPin size={14} /> {cust.address}</span>}
                  {cust?.phone && <span><Phone size={14} /> {cust.phone}</span>}
                </div>
                {v.notes && (
                  <div style={styles.jobNoteRow}>
                    <StickyNote size={13} style={styles.jobNoteIcon} />
                    <span>{v.notes}</span>
                  </div>
                )}
                <div style={styles.jobActions}>
                  {v.status === "scheduled" && (
                    <button style={styles.linkBtn} onClick={() => onStatusChange(v, "completed")}><CheckCircle2 size={14} /> Mark visited</button>
                  )}
                  {v.status === "completed" && (
                    <button style={{ ...styles.linkBtn, color: "#3A7D44" }} onClick={() => onStatusChange(v, "converted")}><ClipboardCheck size={14} /> Mark converted</button>
                  )}
                  {(v.status === "scheduled") && (
                    <button style={{ ...styles.linkBtn, color: "#C9694F" }} onClick={() => onStatusChange(v, "cancelled")}><Ban size={14} /> Cancel</button>
                  )}
                  <button style={styles.linkBtn} onClick={() => onEdit(v)}><Edit2 size={14} /> Edit</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuoteModal({ visit, customers, members, onSave, onDelete, onClose }) {
  const [form, setForm] = useState(visit);
  const allMembers = [...new Set(members.map(m => m.name))];

  return (
    <Modal title={visit.id ? "Edit quick visit" : "New quick visit"} onClose={onClose}>
      <Field label="Client">
        <select style={styles.input} value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value })}>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Assigned member">
        <select style={styles.input} value={form.member} onChange={e => setForm({ ...form, member: e.target.value })}>
          <option value="">— Unassigned —</option>
          {allMembers.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </Field>
      <div style={{ display: "flex", gap: 12 }}>
        <Field label="Date"><input type="date" style={styles.input} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
        <Field label="Time"><input type="time" style={styles.input} value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} /></Field>
      </div>
      <Field label="Status">
        <select style={styles.input} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
          {Object.entries(QUOTE_STATUS_STYLES).map(([key, s]) => <option key={key} value={key}>{s.label}</option>)}
        </select>
      </Field>
      <Field label="Notes (quote amount, client interest, observations)">
        <textarea style={{ ...styles.input, minHeight: 80 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <div style={styles.modalActions}>
        {visit.id && <button style={styles.dangerBtn} onClick={() => onDelete(visit.id)}><Trash2 size={15} /> Delete</button>}
        <div style={{ flex: 1 }} />
        <button style={styles.secondaryBtnSm} onClick={onClose}>Cancel</button>
        <button style={styles.primaryBtn} onClick={() => onSave(form)} disabled={!form.customerId}>Save visit</button>
      </div>
    </Modal>
  );
}

// ---------- Teams ----------
function TeamsView({ teams, jobs, onNew, onEdit }) {
  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Teams</h1>
          <p style={styles.pageSub}>Manage crews and their members.</p>
        </div>
        <button style={styles.primaryBtn} onClick={onNew}><Plus size={16} /> New team</button>
      </div>

      <div style={styles.customerGrid}>
        {teams.map(t => {
          const activeJobs = jobs.filter(j => j.teamId === t.id && (j.status === "scheduled" || j.status === "in-progress")).length;
          const completedJobs = jobs.filter(j => j.teamId === t.id && j.status === "completed").length;
          return (
            <div key={t.id} style={styles.customerCard}>
              <div style={styles.customerCardHeader}>
                <div style={{ ...styles.teamColorSwatch, background: t.color }} />
                <div style={{ flex: 1 }}>
                  <div style={styles.customerName}>{t.name}</div>
                  <div style={styles.customerPhone}>{t.members.length} member{t.members.length !== 1 ? "s" : ""}</div>
                </div>
                <button style={styles.iconBtn} onClick={() => onEdit(t)}><Edit2 size={15} /></button>
              </div>
              <div style={styles.teamMemberList}>
                {t.members.length === 0 && <span style={styles.teamMemberEmpty}>No members added yet</span>}
                {[...t.members].sort(alphabetical).map((m, i) => (
                  <span key={i} style={styles.teamMemberChip}>{m}</span>
                ))}
              </div>
              <div style={styles.customerStatsRow}>
                <div><div style={styles.customerStatNum}>{activeJobs}</div><div style={styles.customerStatLabel}>Upcoming jobs</div></div>
                <div><div style={styles.customerStatNum}>{completedJobs}</div><div style={styles.customerStatLabel}>Completed</div></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Members ----------
function MembersView({ members, teams, jobs, onNew, onEdit }) {
  function teamsForMember(name) {
    return teams.filter(t => t.members.includes(name));
  }

  const sortedMembers = useMemo(() => [...members].sort(byName), [members]);

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Members</h1>
          <p style={styles.pageSub}>Contact details and history for everyone on your crews.</p>
        </div>
        <button style={styles.primaryBtn} onClick={onNew}><Plus size={16} /> New member</button>
      </div>

      <div style={styles.customerGrid}>
        {sortedMembers.map(m => {
          const memberTeams = teamsForMember(m.name);
          const jobsWorked = jobs.filter(j => (j.assignments || []).some(a => a.member === m.name));
          const completedCount = jobsWorked.filter(j => j.status === "completed").length;
          const totalEarned = jobsWorked.reduce((s, j) => {
            const a = (j.assignments || []).find(a => a.member === m.name);
            return s + (Number(a?.amount) || 0);
          }, 0);
          return (
            <div key={m.id} style={styles.customerCard}>
              <div style={styles.customerCardHeader}>
                <div style={styles.avatarCircle}>{m.name.split(" ").map(p => p[0]).slice(0, 2).join("")}</div>
                <div style={{ flex: 1 }}>
                  <div style={styles.customerName}>{m.name}</div>
                  <div style={styles.customerPhone}><Phone size={12} /> {m.phone || "No phone on file"}</div>
                </div>
                <button style={styles.iconBtn} onClick={() => onEdit(m)}><Edit2 size={15} /></button>
              </div>
              {m.address && <div style={styles.customerAddress}><MapPin size={13} /> {m.address}</div>}
              {m.hireDate && (
                <div style={styles.memberHireDate}>
                  Hired {new Date(m.hireDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </div>
              )}
              {memberTeams.length > 0 && (
                <div style={styles.teamMemberList}>
                  {memberTeams.map(t => (
                    <span key={t.id} style={{ ...styles.teamMemberChip, background: t.color + "1A", color: t.color }}>{t.name}</span>
                  ))}
                </div>
              )}
              {m.notes && <div style={styles.customerNotes}>{m.notes}</div>}
              <div style={styles.customerStatsRow}>
                <div><div style={styles.customerStatNum}>{completedCount}</div><div style={styles.customerStatLabel}>Jobs completed</div></div>
                <div><div style={styles.customerStatNum}>{fmtMoney(totalEarned)}</div><div style={styles.customerStatLabel}>Lifetime earned</div></div>
              </div>
            </div>
          );
        })}
        {members.length === 0 && <div style={styles.emptyState}>No members yet. Add your first crew member to get started.</div>}
      </div>
    </div>
  );
}

// ---------- Customers ----------
function CustomersView({ customers, jobs, payments, teamMap, onNew, onEdit, onHistory }) {
  const [subTab, setSubTab] = useState("all");

  const sortedCustomers = useMemo(() => [...customers].sort(byName), [customers]);

  const filtered = useMemo(() => {
    if (subTab === "all") return sortedCustomers;
    return sortedCustomers.filter(c => (c.clientStatus || "new") === subTab);
  }, [sortedCustomers, subTab]);

  const newCount = customers.filter(c => (c.clientStatus || "new") === "new").length;
  const returningCount = customers.filter(c => c.clientStatus === "returning").length;

  function ClientCard({ c }) {
    const cJobs = jobs.filter(j => j.customerId === c.id);
    const completed = cJobs.filter(j => j.status === "completed").length;
    const totalPaid = payments.filter(p => p.customerId === c.id).reduce((s, p) => s + Number(p.amount), 0);
    const isNew = (c.clientStatus || "new") === "new";
    return (
      <div style={styles.customerCard}>
        <div style={styles.customerCardHeader}>
          <div style={styles.avatarCircle}>{c.name.split(" ").map(p => p[0]).slice(0, 2).join("")}</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div style={styles.customerName}>{c.name}</div>
              <span style={{ ...styles.pill, background: isNew ? "#E4F0FA" : "#E8F5E2", color: isNew ? "#1E6FA8" : "#3A7D44", fontSize: 10.5 }}>
                {isNew ? "New" : "Previous"}
              </span>
            </div>
            <div style={styles.customerPhone}><Phone size={12} /> {c.phone}</div>
          </div>
          <button style={styles.iconBtn} onClick={() => onEdit(c)}><Edit2 size={15} /></button>
        </div>
        <div style={styles.customerAddress}><MapPin size={13} /> {c.address}</div>
        {c.notes && <div style={styles.customerNotes}>{c.notes}</div>}
        <div style={styles.customerStatsRow}>
          <div><div style={styles.customerStatNum}>{completed}</div><div style={styles.customerStatLabel}>Services done</div></div>
          <div><div style={styles.customerStatNum}>{fmtMoney(totalPaid)}</div><div style={styles.customerStatLabel}>Lifetime paid</div></div>
        </div>
        <button style={styles.secondaryBtn} onClick={() => onHistory(c)}>View full history</button>
      </div>
    );
  }

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Clients</h1>
          <p style={styles.pageSub}>Client details and full service history.</p>
        </div>
        <button style={styles.primaryBtn} onClick={onNew}><Plus size={16} /> New client</button>
      </div>

      <div style={styles.clientSubTabRow}>
        <button onClick={() => setSubTab("all")} style={{ ...styles.clientSubTab, ...(subTab === "all" ? styles.clientSubTabActive : {}) }}>
          All <span style={styles.clientSubTabCount}>{customers.length}</span>
        </button>
        <button onClick={() => setSubTab("new")} style={{ ...styles.clientSubTab, ...(subTab === "new" ? styles.clientSubTabActive : {}) }}>
          New <span style={styles.clientSubTabCount}>{newCount}</span>
        </button>
        <button onClick={() => setSubTab("returning")} style={{ ...styles.clientSubTab, ...(subTab === "returning" ? styles.clientSubTabActive : {}) }}>
          Previous <span style={styles.clientSubTabCount}>{returningCount}</span>
        </button>
      </div>

      {filtered.length === 0 && (
        <div style={styles.emptyState}>No {subTab === "all" ? "" : subTab === "returning" ? "previous" : subTab} clients yet.</div>
      )}

      <div style={styles.customerGrid}>
        {filtered.map(c => <ClientCard key={c.id} c={c} />)}
      </div>
    </div>
  );
}

// ---------- Payments ----------
function PaymentsView({ payments, customerMap, customers, jobs, teams, onNew, onEdit }) {
  const methodIcon = { Cash: <Banknote size={14} />, Card: <CreditCard size={14} />, "Bank Transfer": <Wallet size={14} />, Check: <Wallet size={14} />, Online: <Wallet size={14} /> };

  const jobMap = useMemo(() => Object.fromEntries(jobs.map(j => [j.id, j])), [jobs]);
  const teamMap = useMemo(() => Object.fromEntries(teams.map(t => [t.id, t])), [teams]);

  function teamForPayment(p) {
    const job = p.jobId ? jobMap[p.jobId] : null;
    return job ? teamMap[job.teamId] : null;
  }

  const [methodFilter, setMethodFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Alphabetical by client by default; newest-first is still one click away, since payment
  // history is often read by "what came in lately".
  const [sortBy, setSortBy] = useState("client");

  const filtered = useMemo(() => {
    const nameOf = p => customerMap[p.customerId]?.name || "";
    return payments
      .filter(p => methodFilter === "all" || p.method === methodFilter)
      .filter(p => {
        if (teamFilter === "all") return true;
        if (teamFilter === "none") return !teamForPayment(p);
        return teamForPayment(p)?.id === teamFilter;
      })
      .filter(p => !startDate || p.date >= startDate)
      .filter(p => !endDate || p.date <= endDate)
      .sort((a, b) => sortBy === "client"
        ? alphabetical(nameOf(a), nameOf(b)) || b.date.localeCompare(a.date)
        : b.date.localeCompare(a.date) || alphabetical(nameOf(a), nameOf(b))
      );
  }, [payments, methodFilter, teamFilter, startDate, endDate, sortBy, customerMap, jobMap, teamMap]);

  const filteredTotal = filtered.reduce((s, p) => s + Number(p.amount), 0);
  const hasActiveFilters = methodFilter !== "all" || teamFilter !== "all" || startDate || endDate;

  function clearFilters() {
    setMethodFilter("all");
    setTeamFilter("all");
    setStartDate("");
    setEndDate("");
  }

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Payment history</h1>
          <p style={styles.pageSub}>Every payment, filterable by method, team, and date.</p>
        </div>
        <button style={styles.primaryBtn} onClick={onNew}><Plus size={16} /> Log payment</button>
      </div>

      {/* Now that each filter is one compact control, method and team share a row. */}
      <div style={styles.filterRow}>
        <span style={styles.filterRowLabel}><Filter size={13} /> Method</span>
        <SelectFilter
          label="Filter by payment method"
          value={methodFilter}
          onChange={setMethodFilter}
          options={[
            { value: "all", label: "All methods" },
            ...PAYMENT_METHODS.map(m => ({ value: m, label: m })),
          ]}
        />
        <span style={{ ...styles.filterRowLabel, marginLeft: 8 }}><Users size={13} /> Team</span>
        <TeamSelect
          teams={teams}
          value={teamFilter}
          onChange={setTeamFilter}
          allLabel="All teams"
          extraOptions={[{ value: "none", label: "Not linked to a job" }]}
        />
      </div>

      <div style={styles.filterRow}>
        <span style={styles.filterRowLabel}><CalendarDays size={13} /> Date</span>
        <input type="date" style={styles.dateInput} value={startDate} max={endDate || undefined} onChange={e => setStartDate(e.target.value)} />
        <span style={styles.filterRowLabel}>to</span>
        <input type="date" style={styles.dateInput} value={endDate} min={startDate || undefined} onChange={e => setEndDate(e.target.value)} />
        {hasActiveFilters && <button style={styles.clearDayBtn} onClick={clearFilters}><X size={13} /> Clear filters</button>}
      </div>

      <div style={styles.filterRow}>
        <span style={styles.filterRowLabel}>Sort</span>
        <SelectFilter
          label="Sort order"
          value={sortBy}
          onChange={setSortBy}
          activeWhen={false}
          options={[
            { value: "client", label: "Client A–Z" },
            { value: "date", label: "Newest first" },
          ]}
        />
      </div>

      {hasActiveFilters && (
        <div style={styles.selectedDayBanner}>
          <Filter size={15} />
          <span>{filtered.length} payment{filtered.length !== 1 ? "s" : ""} matching filters · total <strong>{fmtMoney(filteredTotal)}</strong></span>
        </div>
      )}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Client</th>
              <th style={styles.th}>Team</th>
              <th style={styles.th}>Method</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Amount</th>
              <th style={styles.th}>Notes</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const team = teamForPayment(p);
              return (
                <tr key={p.id} style={styles.tr} onClick={() => onEdit(p)}>
                  <td style={styles.td}>{fmtDate(p.date)}</td>
                  <td style={styles.td}>{customerMap[p.customerId]?.name || "—"}</td>
                  <td style={styles.td}>
                    {team ? (
                      <span style={styles.methodTag}><span style={{ ...styles.teamDot, background: team.color }} /> {team.name}</span>
                    ) : (
                      <span style={{ color: "#B7C2BD" }}>—</span>
                    )}
                  </td>
                  <td style={styles.td}><span style={styles.methodTag}>{methodIcon[p.method]} {p.method}</span></td>
                  <td style={{ ...styles.td, textAlign: "right", fontWeight: 600 }}>{fmtMoney(Number(p.amount))}</td>
                  <td style={{ ...styles.td, color: "#8A9590", fontStyle: p.notes ? "normal" : "italic" }}>{p.notes || "—"}</td>
                  <td style={{ ...styles.td, width: 28 }}><Edit2 size={14} color="#9aa5a2" /></td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td style={styles.td} colSpan={7}>{payments.length === 0 ? "No payments recorded yet." : "No payments match these filters."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Invoices ----------
function InvoicesView({ invoices, customerMap, onNew, onEdit, onPrint }) {
  // Alphabetical by client by default, newest-first on request.
  const [sortBy, setSortBy] = useState("client");
  const [clientFilter, setClientFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Only clients who actually have an invoice are offered, so the list stays short and every
  // option returns at least one row.
  const invoiceClients = useMemo(() => {
    const seen = new Map();
    invoices.forEach(inv => {
      const c = customerMap[inv.customerId];
      if (c && !seen.has(c.id)) seen.set(c.id, c);
    });
    return [...seen.values()].sort(byName);
  }, [invoices, customerMap]);

  const sorted = useMemo(() => {
    const nameOf = inv => customerMap[inv.customerId]?.name || "";
    return invoices
      .filter(inv => clientFilter === "all" || inv.customerId === clientFilter)
      .filter(inv => statusFilter === "all" || (inv.status || "unpaid") === statusFilter)
      .sort((a, b) => sortBy === "client"
        ? alphabetical(nameOf(a), nameOf(b)) || (b.issueDate || "").localeCompare(a.issueDate || "")
        : (b.issueDate || "").localeCompare(a.issueDate || "") || alphabetical(nameOf(a), nameOf(b))
      );
  }, [invoices, sortBy, clientFilter, statusFilter, customerMap]);

  const filteredTotal = sorted.reduce((s, inv) => s + (Number(inv.total) || 0), 0);
  const hasActiveFilters = clientFilter !== "all" || statusFilter !== "all";

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Invoices</h1>
          <p style={styles.pageSub}>Generate a printable invoice for a client from their jobs.</p>
        </div>
        <button style={styles.primaryBtn} onClick={onNew}><Plus size={16} /> New invoice</button>
      </div>

      <div style={styles.filterRow}>
        <span style={styles.filterRowLabel}><Users size={13} /> Client</span>
        <SelectFilter
          label="Filter by client"
          value={clientFilter}
          onChange={setClientFilter}
          width={200}
          options={[
            { value: "all", label: "All clients" },
            ...invoiceClients.map(c => ({ value: c.id, label: c.name })),
          ]}
        />
        <span style={{ ...styles.filterRowLabel, marginLeft: 8 }}><Filter size={13} /> Status</span>
        <SelectFilter
          label="Filter by invoice status"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: "all", label: "All statuses" },
            ...Object.keys(INVOICE_STATUS_STYLES).map(s => ({ value: s, label: INVOICE_STATUS_STYLES[s].label })),
          ]}
        />
      </div>

      <div style={styles.filterRow}>
        <span style={styles.filterRowLabel}>Sort</span>
        <SelectFilter
          label="Sort order"
          value={sortBy}
          onChange={setSortBy}
          activeWhen={false}
          options={[
            { value: "client", label: "Client A–Z" },
            { value: "date", label: "Newest first" },
          ]}
        />
        {hasActiveFilters && (
          <button style={styles.clearDayBtn} onClick={() => { setClientFilter("all"); setStatusFilter("all"); }}>
            <X size={13} /> Clear filters
          </button>
        )}
      </div>

      {hasActiveFilters && (
        <div style={styles.selectedDayBanner}>
          <Filter size={15} />
          <span>{sorted.length} invoice{sorted.length !== 1 ? "s" : ""} matching filters · total <strong>{fmtMoney(filteredTotal)}</strong></span>
        </div>
      )}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Invoice #</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Client</th>
              <th style={styles.th}>Items</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Total</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(inv => {
              const st = INVOICE_STATUS_STYLES[inv.status] || INVOICE_STATUS_STYLES.unpaid;
              return (
                <tr key={inv.id} style={styles.tr}>
                  <td style={{ ...styles.td, fontWeight: 600 }} onClick={() => onEdit(inv)}>{inv.invoiceNumber || "—"}</td>
                  <td style={styles.td} onClick={() => onEdit(inv)}>{inv.issueDate ? fmtDate(inv.issueDate) : "—"}</td>
                  <td style={styles.td} onClick={() => onEdit(inv)}>{customerMap[inv.customerId]?.name || "—"}</td>
                  <td style={styles.td} onClick={() => onEdit(inv)}>{(inv.lineItems || []).length}</td>
                  <td style={{ ...styles.td, textAlign: "right", fontWeight: 600 }} onClick={() => onEdit(inv)}>{fmtMoney(Number(inv.total) || 0)}</td>
                  <td style={styles.td} onClick={() => onEdit(inv)}><Pill bg={st.bg} color={st.color}>{st.label}</Pill></td>
                  <td style={{ ...styles.td, width: 80 }}>
                    <button style={styles.linkBtn} onClick={(e) => { e.stopPropagation(); onPrint(inv); }}><Printer size={14} /> Print</button>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td style={styles.td} colSpan={7}>{invoices.length === 0 ? "No invoices yet. Click “New invoice” to generate one." : "No invoices match these filters."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvoiceModal({ invoice, customers, jobs, invoices, onSave, onDelete, onClose, onSaveAndPrint }) {
  const [form, setForm] = useState(invoice);
  const total = useMemo(
    () => (form.lineItems || []).reduce((s, li) => s + (Number(li.amount) || 0), 0),
    [form.lineItems]
  );

  function setLine(idx, patch) {
    setForm(f => ({ ...f, lineItems: f.lineItems.map((li, i) => i === idx ? { ...li, ...patch } : li) }));
  }
  function removeLine(idx) {
    setForm(f => ({ ...f, lineItems: f.lineItems.filter((_, i) => i !== idx) }));
  }
  function addBlankLine() {
    setForm(f => ({ ...f, lineItems: [...f.lineItems, { date: f.issueDate, description: "", amount: "" }] }));
  }
  // Append the selected client's jobs within the chosen range as line items.
  function addJobsInRange() {
    const matches = jobs
      .filter(j => j.customerId === form.customerId)
      .filter(j => !form.periodStart || j.date >= form.periodStart)
      .filter(j => !form.periodEnd || j.date <= form.periodEnd)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(j => ({ date: j.date, description: j.service, amount: jobAmount(j) }));
    if (matches.length === 0) {
      setForm(f => ({ ...f, _hint: "No jobs found for this client in that date range." }));
      return;
    }
    setForm(f => ({ ...f, lineItems: [...f.lineItems, ...matches], _hint: null }));
  }

  const built = { ...form, total };
  const canSave = form.customerId && form.lineItems.length > 0;

  return (
    <Modal title={invoice.id ? `Edit ${invoice.invoiceNumber || "invoice"}` : "New invoice"} onClose={onClose} wide>
      <Field label="Client">
        <select style={styles.input} value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value })}>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <div style={{ display: "flex", gap: 12 }}>
        <Field label="Invoice date"><input type="date" style={styles.input} value={form.issueDate} onChange={e => setForm({ ...form, issueDate: e.target.value })} /></Field>
        <Field label="Status">
          <select style={styles.input} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
            {Object.keys(INVOICE_STATUS_STYLES).map(s => <option key={s} value={s}>{INVOICE_STATUS_STYLES[s].label}</option>)}
          </select>
        </Field>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
        <Field label="From"><input type="date" style={styles.input} value={form.periodStart || ""} max={form.periodEnd || undefined} onChange={e => setForm({ ...form, periodStart: e.target.value })} /></Field>
        <Field label="To"><input type="date" style={styles.input} value={form.periodEnd || ""} min={form.periodStart || undefined} onChange={e => setForm({ ...form, periodEnd: e.target.value })} /></Field>
        <div style={{ marginBottom: 14 }}>
          <button style={styles.secondaryBtnSm} onClick={addJobsInRange}><Plus size={15} /> Add jobs in range</button>
        </div>
      </div>
      {form._hint && <p style={{ ...styles.pageSub, color: "#C9694F", marginTop: -6 }}>{form._hint}</p>}

      <label style={styles.label}>Line items</label>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
        {form.lineItems.map((li, idx) => (
          <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="date" style={{ ...styles.input, flex: "0 0 140px" }} value={li.date || ""} onChange={e => setLine(idx, { date: e.target.value })} />
            <input style={{ ...styles.input, flex: 1 }} placeholder="Description" value={li.description} onChange={e => setLine(idx, { description: e.target.value })} />
            <input type="number" step="0.01" style={{ ...styles.input, flex: "0 0 110px" }} placeholder="0.00" value={li.amount} onChange={e => setLine(idx, { amount: e.target.value })} />
            <button style={styles.iconBtn} onClick={() => removeLine(idx)}><X size={16} /></button>
          </div>
        ))}
        {form.lineItems.length === 0 && <p style={styles.pageSub}>No line items yet. Add jobs in a date range above, or add a blank line.</p>}
      </div>
      <button style={styles.linkBtn} onClick={addBlankLine}><Plus size={14} /> Add line</button>

      <Field label="Notes (shown on the invoice)">
        <textarea style={{ ...styles.input, minHeight: 60 }} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} />
      </Field>

      <div style={{ textAlign: "right", fontWeight: 700, fontSize: 16, marginTop: 4 }}>
        Total: {fmtMoney(total)}
      </div>

      <div style={styles.modalActions}>
        {invoice.id && <button style={styles.dangerBtn} onClick={() => onDelete(invoice.id)}><Trash2 size={15} /> Delete</button>}
        <div style={{ flex: 1 }} />
        <button style={styles.secondaryBtnSm} onClick={onClose}>Cancel</button>
        <button style={styles.secondaryBtnSm} onClick={() => onSave(built)} disabled={!canSave}>Save</button>
        <button style={styles.primaryBtn} onClick={() => onSaveAndPrint(built)} disabled={!canSave}><Printer size={15} /> Save &amp; print</button>
      </div>
    </Modal>
  );
}

function InvoicePrintView({ invoice, customer, businessInfo, onClose }) {
  const biz = businessInfo || DEFAULT_BUSINESS_INFO;
  const total = (invoice.lineItems || []).reduce((s, li) => s + (Number(li.amount) || 0), 0);
  const issued = invoice.issueDate ? new Date(invoice.issueDate + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

  // "idle" | "sending" | "sent" | "error"
  const [emailState, setEmailState] = useState("idle");
  const [emailError, setEmailError] = useState("");

  async function sendEmail() {
    if (!customer?.email) {
      setEmailState("error");
      setEmailError("This client has no email address. Add one on the client first.");
      return;
    }
    setEmailState("sending");
    setEmailError("");
    try {
      await emailInvoice({
        toEmail: customer.email,
        invoice: { ...invoice, total },
        customer,
        business: biz,
      });
      setEmailState("sent");
    } catch (err) {
      setEmailState("error");
      setEmailError(err.message || "Failed to send email.");
    }
  }

  return (
    <div style={styles.printOverlay} className="cs-print-overlay">
      <div style={styles.printToolbar} className="no-print">
        <button style={styles.secondaryBtnSm} onClick={onClose}><X size={15} /> Close</button>
        <button
          style={{ ...styles.secondaryBtnSm, opacity: emailState === "sending" ? 0.6 : 1 }}
          onClick={sendEmail}
          disabled={emailState === "sending"}
          title={customer?.email ? `Send to ${customer.email}` : "This client has no email address"}
        >
          <Mail size={15} /> {emailState === "sending" ? "Sending…" : emailState === "sent" ? "Sent ✓" : "Email to client"}
        </button>
        <button style={styles.primaryBtn} onClick={() => window.print()}><Printer size={15} /> Print / Save PDF</button>
        {emailState === "sent" && (
          <span style={{ color: "#1E6FA8", fontSize: 13, alignSelf: "center" }}>
            Invoice emailed to {customer?.email}.
          </span>
        )}
        {emailState === "error" && (
          <span style={{ color: "#c0392b", fontSize: 13, alignSelf: "center" }}>{emailError}</span>
        )}
      </div>
      <div id="invoice-print" style={styles.invoiceSheet}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#1d3a32" }}>{biz.name}</div>
            {biz.tagline && <div style={{ color: "#6b7975", fontSize: 13 }}>{biz.tagline}</div>}
            {biz.address && <div style={{ color: "#6b7975", fontSize: 13 }}>{biz.address}</div>}
            {biz.phone && <div style={{ color: "#6b7975", fontSize: 13 }}>{biz.phone}</div>}
            {biz.email && <div style={{ color: "#6b7975", fontSize: 13 }}>{biz.email}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 1 }}>INVOICE</div>
            <div style={{ color: "#6b7975", fontSize: 13 }}>{invoice.invoiceNumber}</div>
            <div style={{ color: "#6b7975", fontSize: 13 }}>{issued}</div>
          </div>
        </div>

        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#9aa5a2", marginBottom: 4 }}>Bill to</div>
          <div style={{ fontWeight: 600 }}>{customer?.name || "—"}</div>
          {customer?.address && <div style={{ color: "#6b7975", fontSize: 13 }}>{customer.address}</div>}
          {customer?.phone && <div style={{ color: "#6b7975", fontSize: 13 }}>{customer.phone}</div>}
          {customer?.email && <div style={{ color: "#6b7975", fontSize: 13 }}>{customer.email}</div>}
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 18 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #1d3a32" }}>
              <th style={styles.invTh}>Date</th>
              <th style={styles.invTh}>Description</th>
              <th style={{ ...styles.invTh, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lineItems || []).map((li, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #e5ebe8" }}>
                <td style={styles.invTd}>{li.date ? fmtDate(li.date) : "—"}</td>
                <td style={styles.invTd}>{li.description || "—"}</td>
                <td style={{ ...styles.invTd, textAlign: "right" }}>{fmtMoney(Number(li.amount) || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24 }}>
          <div style={{ minWidth: 220 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 17, borderTop: "2px solid #1d3a32", paddingTop: 8 }}>
              <span>Total due</span><span>{fmtMoney(total)}</span>
            </div>
          </div>
        </div>

        {invoice.notes && (
          <div style={{ borderTop: "1px solid #e5ebe8", paddingTop: 12 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, color: "#9aa5a2", marginBottom: 4 }}>Notes</div>
            <div style={{ color: "#4b5a55", fontSize: 13, whiteSpace: "pre-wrap" }}>{invoice.notes}</div>
          </div>
        )}

        <div style={{ marginTop: 36, textAlign: "center", color: "#9aa5a2", fontSize: 12 }}>Thank you for your business.</div>
      </div>
    </div>
  );
}

// ---------- Settings ----------
function SettingsView({ businessInfo, onSave }) {
  const [form, setForm] = useState(businessInfo);
  const [saved, setSaved] = useState(false);

  function submit(e) {
    e.preventDefault();
    onSave(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={styles.pageTitle}>Settings</h1>
        <p style={styles.pageSub}>Business details shown on printed invoices.</p>
      </div>
      <form
        onSubmit={submit}
        style={{ maxWidth: 480, background: "#fff", border: "1.5px solid #E6EEF1", borderRadius: 12, padding: 22 }}
      >
        <Field label="Business name">
          <input style={styles.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Tagline">
          <input style={styles.input} value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })} />
        </Field>
        <Field label="Address">
          <input style={styles.input} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
        </Field>
        <Field label="Phone">
          <input style={styles.input} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
        </Field>
        <Field label="Email">
          <input style={styles.input} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        </Field>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
          <button type="submit" style={styles.primaryBtn}>Save</button>
          {saved && <span style={{ color: "#1E6FA8", fontSize: 13 }}>Saved.</span>}
        </div>
      </form>
    </div>
  );
}

// ---------- Reports ----------
function ReportsView({ payments, expenses, jobs, teams, customers, onNewExpense, onEditExpense }) {
  const today = easternDateStr();
  const thirtyDaysAgo = easternDateStr(new Date(Date.now() - 30 * 86400000));
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);

  const filteredPayments = useMemo(() => payments.filter(p => p.date >= startDate && p.date <= endDate), [payments, startDate, endDate]);
  const filteredExpenses = useMemo(() => expenses.filter(e => e.date >= startDate && e.date <= endDate), [expenses, startDate, endDate]);
  const filteredJobs = useMemo(() => jobs.filter(j => j.date >= startDate && j.date <= endDate), [jobs, startDate, endDate]);

  // Income counts every payment logged on the Payments tab, whatever the method
  // (Cash, Card, Check, Bank Transfer, Online) — they're all money in.
  const totalIncome = filteredPayments.reduce((s, p) => s + Number(p.amount), 0);
  const totalExpenses = filteredExpenses.reduce((s, e) => s + Number(e.amount), 0);
  const completedCount = filteredJobs.filter(j => j.status === "completed").length;

  // Expenses can be read two ways: day by day (grouped, with a subtotal per day) or as one flat
  // list. Dates are ISO yyyy-mm-dd, so a plain string compare sorts them correctly.
  const [expenseGroup, setExpenseGroup] = useState("date");
  const [expenseSort, setExpenseSort] = useState("desc");

  const sortedExpenses = useMemo(
    () => [...filteredExpenses].sort((a, b) =>
      expenseSort === "desc" ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date)
    ),
    [filteredExpenses, expenseSort]
  );

  // Keys are non-numeric strings, so Object.entries preserves the insertion order set above.
  const expensesByDate = useMemo(() => {
    const m = {};
    sortedExpenses.forEach(e => {
      if (!m[e.date]) m[e.date] = [];
      m[e.date].push(e);
    });
    return Object.entries(m).map(([date, items]) => ({
      date,
      // Days stay in date order; the entries within a day read alphabetically.
      items: [...items].sort((a, b) => alphabetical(a.description, b.description)),
      total: items.reduce((s, e) => s + Number(e.amount), 0),
    }));
  }, [sortedExpenses]);

  const maxDayExpense = Math.max(1, ...expensesByDate.map(d => d.total));

  // Every breakdown below reads A–Z by default; flip to "Amount" to rank by size instead.
  const [rankBy, setRankBy] = useState("name");
  const rank = (nameA, nameB, valA, valB) =>
    rankBy === "name" ? alphabetical(nameA, nameB) : valB - valA;

  const byCategory = useMemo(() => {
    const m = {};
    filteredExpenses.forEach(e => { m[e.category] = (m[e.category] || 0) + Number(e.amount); });
    return Object.entries(m).sort((a, b) => rank(a[0], b[0], a[1], b[1]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredExpenses, rankBy]);
  const maxCat = Math.max(1, ...byCategory.map(([, v]) => v));

  const byMethod = useMemo(() => {
    const m = {};
    filteredPayments.forEach(p => { m[p.method] = (m[p.method] || 0) + Number(p.amount); });
    return Object.entries(m).sort((a, b) => rank(a[0], b[0], a[1], b[1]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPayments, rankBy]);
  const maxMethod = Math.max(1, ...byMethod.map(([, v]) => v));

  const [reportView, setReportView] = useState("overview");
  const [clientTypeFilter, setClientTypeFilter] = useState("all");

  // Revenue attribution: a payment counts toward a team only if it's linked to a job (jobId) for that team.
  const paymentsByJobId = useMemo(() => {
    const m = {};
    filteredPayments.forEach(p => { if (p.jobId) m[p.jobId] = (m[p.jobId] || 0) + Number(p.amount); });
    return m;
  }, [filteredPayments]);

  const unattributedIncome = useMemo(
    () => filteredPayments.filter(p => !p.jobId).reduce((s, p) => s + Number(p.amount), 0),
    [filteredPayments]
  );

  const byTeam = useMemo(() => {
    return teams.map(t => {
      const teamJobs = filteredJobs.filter(j => j.teamId === t.id);
      const completed = teamJobs.filter(j => j.status === "completed").length;
      const scheduled = teamJobs.filter(j => j.status === "scheduled" || j.status === "in-progress").length;
      const cancelled = teamJobs.filter(j => j.status === "cancelled").length;
      const revenue = teamJobs.reduce((s, j) => s + (paymentsByJobId[j.id] || 0), 0);
      return { team: t, totalJobs: teamJobs.length, completed, scheduled, cancelled, revenue };
    }).sort((a, b) => rank(a.team.name, b.team.name, a.revenue, b.revenue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, filteredJobs, paymentsByJobId, rankBy]);

  const maxTeamRevenue = Math.max(1, ...byTeam.map(t => t.revenue));

  // Member breakdown follows the money, not the schedule: a member is credited for a job only
  // once the client's payment for it is logged on the Payments tab, and it lands in whichever
  // range that payment falls in — not the job's own date. Jobs carry no price of their own, so
  // a job counts as paid as soon as any payment is linked to it, and the pay credited is the
  // per-person amount set on that job. Looked up against all jobs, since a job worked before
  // the range can still be paid inside it.
  const jobById = useMemo(() => Object.fromEntries(jobs.map(j => [j.id, j])), [jobs]);

  const paidJobs = useMemo(() => {
    const seen = new Map();
    filteredPayments.forEach(p => {
      const job = p.jobId ? jobById[p.jobId] : null;
      if (job && !seen.has(job.id)) seen.set(job.id, job);
    });
    return [...seen.values()];
  }, [filteredPayments, jobById]);

  const byMember = useMemo(() => {
    const m = {};
    paidJobs.forEach(j => {
      const team = teams.find(t => t.id === j.teamId);
      (j.assignments || []).forEach(a => {
        const key = `${j.teamId}::${a.member}`;
        if (!m[key]) {
          m[key] = { name: a.member, teamName: team?.name || "Unassigned", teamColor: team?.color || "#9AA9A3", jobs: 0, completed: 0, payout: 0 };
        }
        m[key].jobs += 1;
        if (j.status === "completed") m[key].completed += 1;
        m[key].payout += Number(a.amount) || 0;
      });
    });
    return Object.values(m).sort((a, b) => rank(a.name, b.name, a.payout, b.payout));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidJobs, teams, rankBy]);

  const maxMemberPayout = Math.max(1, ...byMember.map(m => m.payout));
  const totalPayouts = byMember.reduce((s, m) => s + m.payout, 0);

  // Two reasons paid work can show no payout, worth calling out so a low total isn't mistaken
  // for a real one: the payment was never linked to a job, or the paid job has nobody assigned.
  const unlinkedPaymentCount = useMemo(
    () => filteredPayments.filter(p => !p.jobId || !jobById[p.jobId]).length,
    [filteredPayments, jobById]
  );
  const paidJobsWithoutAssignments = useMemo(
    () => paidJobs.filter(j => !(j.assignments || []).length).length,
    [paidJobs]
  );

  // Net profit discounts every cost: logged expenses plus what members were paid on their jobs.
  // Member pay is toggleable because the same money is sometimes also logged as a "Payroll"
  // expense — counting both would subtract it twice.
  const [includePayouts, setIncludePayouts] = useState(true);
  const payrollExpenses = useMemo(
    () => filteredExpenses.filter(e => e.category === "Payroll").reduce((s, e) => s + Number(e.amount), 0),
    [filteredExpenses]
  );
  const countedPayouts = includePayouts ? totalPayouts : 0;
  const totalCosts = totalExpenses + countedPayouts;
  const netProfit = totalIncome - totalCosts;
  const margin = totalIncome > 0 ? (netProfit / totalIncome) * 100 : 0;
  const doubleCountRisk = includePayouts && payrollExpenses > 0 && totalPayouts > 0;

  // Customer breakdown: payments link to customers directly, so this is a straightforward rollup.
  const byCustomer = useMemo(() => {
    return customers.map(c => {
      const custPayments = filteredPayments.filter(p => p.customerId === c.id);
      const custJobs = filteredJobs.filter(j => j.customerId === c.id);
      const revenue = custPayments.reduce((s, p) => s + Number(p.amount), 0);
      const completed = custJobs.filter(j => j.status === "completed").length;
      const scheduled = custJobs.filter(j => j.status === "scheduled" || j.status === "in-progress").length;
      const cancelled = custJobs.filter(j => j.status === "cancelled").length;
      return { customer: c, revenue, paymentCount: custPayments.length, totalJobs: custJobs.length, completed, scheduled, cancelled };
    })
      .filter(row => row.revenue > 0 || row.totalJobs > 0)
      .sort((a, b) => rank(a.customer.name, b.customer.name, a.revenue, b.revenue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, filteredPayments, filteredJobs, rankBy]);

  const maxCustomerRevenue = Math.max(1, ...byCustomer.map(c => c.revenue));

  function setPreset(days) {
    setEndDate(today);
    setStartDate(easternDateStr(new Date(Date.now() - days * 86400000)));
  }

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Reports</h1>
          <p style={styles.pageSub}>Income vs. expenses, with breakdowns by team, member, and client.</p>
        </div>
        <button style={styles.primaryBtn} onClick={onNewExpense}><Plus size={16} /> Log expense</button>
      </div>

      <div style={styles.filterRow}>
        <button onClick={() => setPreset(7)} style={styles.filterChip}>Last 7 days</button>
        <button onClick={() => setPreset(30)} style={styles.filterChip}>Last 30 days</button>
        <button onClick={() => setPreset(90)} style={styles.filterChip}>Last 90 days</button>
        <span style={styles.dateRangeDivider} />
        <input type="date" style={styles.dateInput} value={startDate} max={endDate} onChange={e => setStartDate(e.target.value)} />
        <span style={styles.filterRowLabel}>to</span>
        <input type="date" style={styles.dateInput} value={endDate} min={startDate} max={today} onChange={e => setEndDate(e.target.value)} />
      </div>

      <div style={styles.filterRow}>
        <button onClick={() => setReportView("overview")} style={{ ...styles.filterChip, ...(reportView === "overview" ? styles.filterChipActive : {}) }}>Overview</button>
        <button onClick={() => setReportView("team")} style={{ ...styles.filterChip, ...(reportView === "team" ? styles.filterChipActive : {}) }}>By team</button>
        <button onClick={() => setReportView("member")} style={{ ...styles.filterChip, ...(reportView === "member" ? styles.filterChipActive : {}) }}>By member</button>
        <button onClick={() => setReportView("customer")} style={{ ...styles.filterChip, ...(reportView === "customer" ? styles.filterChipActive : {}) }}>By client</button>
        <span style={styles.dateRangeDivider} />
        <span style={styles.filterRowLabel}>Sort</span>
        <button onClick={() => setRankBy("name")} style={{ ...styles.filterChip, ...(rankBy === "name" ? styles.filterChipActive : {}) }}>A–Z</button>
        <button onClick={() => setRankBy("amount")} style={{ ...styles.filterChip, ...(rankBy === "amount" ? styles.filterChipActive : {}) }}>Amount</button>
      </div>

      <div style={styles.statsRow}>
        <StatCard icon={<TrendingUp size={18} />} label="Total income" value={fmtMoney(totalIncome)} accent="#1A6B5A" />
        <StatCard icon={<TrendingDown size={18} />} label="Total costs" value={fmtMoney(totalCosts)} accent="#C9694F" />
        <StatCard icon={<Wallet size={18} />} label="Net profit" value={fmtMoney(netProfit)} accent={netProfit >= 0 ? "#1E6FA8" : "#C9694F"} />
        <StatCard icon={<CheckCircle2 size={18} />} label="Jobs completed" value={completedCount} accent="#5B7C99" />
      </div>

      {reportView === "overview" && (
        <>
          <div style={{ ...styles.panel, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
              <div style={styles.panelTitle}>Profit &amp; loss</div>
              <label style={styles.plToggle}>
                <input type="checkbox" checked={includePayouts} onChange={e => setIncludePayouts(e.target.checked)} />
                Subtract member pay
              </label>
            </div>
            <div style={styles.panelSubNote}>
              {fmtDate(startDate)} – {fmtDate(endDate)} · every payment in, every cost out.
            </div>

            <div style={styles.plRow}>
              <span style={styles.plLabel}>Income</span>
              <span style={{ ...styles.plValue, color: "#1A6B5A" }}>{fmtMoney(totalIncome)}</span>
            </div>
            {byMethod.map(([method, val]) => (
              <div key={method} style={styles.plSubRow}>
                <span>{method}</span><span>{fmtMoney(val)}</span>
              </div>
            ))}
            {byMethod.length === 0 && <div style={styles.plSubRow}><span>No payments in this range</span><span>{fmtMoney(0)}</span></div>}

            <div style={{ ...styles.plRow, marginTop: 14 }}>
              <span style={styles.plLabel}>Operating expenses</span>
              <span style={{ ...styles.plValue, color: "#C9694F" }}>−{fmtMoney(totalExpenses)}</span>
            </div>
            {byCategory.map(([cat, val]) => (
              <div key={cat} style={styles.plSubRow}>
                <span>{cat}</span><span>−{fmtMoney(val)}</span>
              </div>
            ))}
            {byCategory.length === 0 && <div style={styles.plSubRow}><span>No expenses logged in this range</span><span>−{fmtMoney(0)}</span></div>}

            <div style={{ ...styles.plRow, marginTop: 14 }}>
              <span style={styles.plLabel}>Member pay{!includePayouts && <span style={styles.plMuted}> (not subtracted)</span>}</span>
              <span style={{ ...styles.plValue, color: includePayouts ? "#C9694F" : "#9AA9A3" }}>
                {includePayouts ? `−${fmtMoney(totalPayouts)}` : fmtMoney(totalPayouts)}
              </span>
            </div>
            <div style={styles.plSubRow}>
              <span>Owed on jobs the client has paid for · {byMember.length} member{byMember.length !== 1 ? "s" : ""} · {paidJobs.length} paid job{paidJobs.length !== 1 ? "s" : ""}</span>
              <span />
            </div>

            <div style={styles.plTotalRow}>
              <span style={styles.plTotalLabel}>Net profit</span>
              <span style={{ ...styles.plTotalValue, color: netProfit >= 0 ? "#1E6FA8" : "#C9694F" }}>{fmtMoney(netProfit)}</span>
            </div>
            <div style={styles.plSubRow}>
              <span>Total costs {fmtMoney(totalCosts)} · margin {totalIncome > 0 ? `${margin.toFixed(1)}%` : "—"}</span>
              <span />
            </div>

            {doubleCountRisk && (
              <div style={{ ...styles.teamWarning, marginTop: 14, marginBottom: 0 }}>
                Heads up: {fmtMoney(payrollExpenses)} is logged under the "Payroll" expense category and {fmtMoney(totalPayouts)} of member pay is also being subtracted. If that's the same money, untick "Subtract member pay" so it isn't counted twice.
              </div>
            )}
          </div>

          <div style={styles.reportGrid}>
            <div style={styles.panel}>
              <div style={styles.panelTitle}>Income by payment method</div>
              {byMethod.length === 0 && <div style={styles.emptyState}>No payments in this range.</div>}
              {byMethod.map(([method, val]) => (
                <div key={method} style={{ marginBottom: 12 }}>
                  <div style={styles.barLabelRow}>
                    <span>{method}</span><span>{fmtMoney(val)}</span>
                  </div>
                  <div style={styles.barTrack}>
                    <div style={{ ...styles.barFill, width: `${(val / maxMethod) * 100}%`, background: "#1A6B5A" }} />
                  </div>
                </div>
              ))}
            </div>

            <div style={styles.panel}>
              <div style={styles.panelTitle}>Expenses by category</div>
              {byCategory.length === 0 && <div style={styles.emptyState}>No expenses in this range.</div>}
              {byCategory.map(([cat, val]) => (
                <div key={cat} style={{ marginBottom: 12 }}>
                  <div style={styles.barLabelRow}>
                    <span>{cat}</span><span>{fmtMoney(val)}</span>
                  </div>
                  <div style={styles.barTrack}>
                    <div style={{ ...styles.barFill, width: `${(val / maxCat) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...styles.panel, marginTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
              <div style={styles.panelTitle}>Expenses in range</div>
              <div style={styles.clientSubTabRow}>
                <button onClick={() => setExpenseGroup("date")} style={{ ...styles.clientSubTab, ...(expenseGroup === "date" ? styles.clientSubTabActive : {}) }}>By date</button>
                <button onClick={() => setExpenseGroup("flat")} style={{ ...styles.clientSubTab, ...(expenseGroup === "flat" ? styles.clientSubTabActive : {}) }}>All</button>
                <button onClick={() => setExpenseSort(s => s === "desc" ? "asc" : "desc")} style={styles.clientSubTab} title="Switch sort order">
                  {expenseSort === "desc" ? "Newest first" : "Oldest first"}
                </button>
              </div>
            </div>
            <div style={styles.panelSubNote}>
              {sortedExpenses.length} expense{sortedExpenses.length !== 1 ? "s" : ""} over {expensesByDate.length} day{expensesByDate.length !== 1 ? "s" : ""} · total <strong>{fmtMoney(totalExpenses)}</strong>
            </div>

            <div style={{ maxHeight: 380, overflowY: "auto" }}>
              {expenseGroup === "date" && expensesByDate.map(({ date, items, total }) => (
                <div key={date} style={styles.expenseDayGroup}>
                  <div style={styles.expenseDayHeader}>
                    <span style={styles.expenseDayDate}>{fmtDate(date)}</span>
                    <span style={styles.expenseDayTotal}>−{fmtMoney(total)}</span>
                  </div>
                  <div style={styles.barTrack}>
                    <div style={{ ...styles.barFill, width: `${(total / maxDayExpense) * 100}%`, background: "#C9694F" }} />
                  </div>
                  {items.map(e => (
                    <div key={e.id} style={styles.expenseRow} onClick={() => onEditExpense(e)}>
                      <div>
                        <div style={styles.expenseDesc}>{e.description}</div>
                        <div style={styles.expenseMeta}>{e.category}</div>
                      </div>
                      <div style={styles.expenseAmount}>−{fmtMoney(Number(e.amount))}</div>
                    </div>
                  ))}
                </div>
              ))}

              {expenseGroup === "flat" && sortedExpenses.map(e => (
                <div key={e.id} style={styles.expenseRow} onClick={() => onEditExpense(e)}>
                  <div>
                    <div style={styles.expenseDesc}>{e.description}</div>
                    <div style={styles.expenseMeta}>{fmtDate(e.date)} · {e.category}</div>
                  </div>
                  <div style={styles.expenseAmount}>−{fmtMoney(Number(e.amount))}</div>
                </div>
              ))}

              {sortedExpenses.length === 0 && <div style={styles.emptyState}>No expenses logged in this range.</div>}
            </div>
          </div>
        </>
      )}

      {reportView === "team" && (
        <div style={styles.panel}>
          <div style={styles.panelTitle}>Performance by team</div>
          {unattributedIncome > 0 && (
            <div style={styles.teamWarning}>
              {fmtMoney(unattributedIncome)} in payments aren't linked to a specific job, so they're excluded from team revenue below. Link payments to a job to attribute them.
            </div>
          )}
          {byTeam.length === 0 && <div style={styles.emptyState}>No teams yet.</div>}
          {byTeam.map(({ team, totalJobs, completed, scheduled, cancelled, revenue }) => (
            <div key={team.id} style={styles.teamReportRow}>
              <div style={styles.teamReportHeader}>
                <span style={{ ...styles.teamDot, background: team.color }} />
                <span style={styles.teamReportName}>{team.name}</span>
                <span style={styles.teamReportRevenue}>{fmtMoney(revenue)}</span>
              </div>
              <div style={styles.barTrack}>
                <div style={{ ...styles.barFill, width: `${(revenue / maxTeamRevenue) * 100}%`, background: team.color }} />
              </div>
              <div style={styles.teamReportMeta}>
                <span>{totalJobs} job{totalJobs !== 1 ? "s" : ""} total</span>
                <span>{completed} completed</span>
                <span>{scheduled} upcoming</span>
                {cancelled > 0 && <span>{cancelled} cancelled</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {reportView === "member" && (
        <div style={styles.panel}>
          <div style={styles.panelTitle}>Payout by member</div>
          <div style={styles.panelSubNote}>
            Total payouts in range: <strong>{fmtMoney(totalPayouts)}</strong> across {paidJobs.length} paid job{paidJobs.length !== 1 ? "s" : ""}.
            Counted from the payments logged on the Payments tab — a member is credited once the client has paid for the job, using the pay you set per person on it.
          </div>
          {(unlinkedPaymentCount > 0 || paidJobsWithoutAssignments > 0) && (
            <div style={styles.teamWarning}>
              {unlinkedPaymentCount > 0 && (
                <div>{unlinkedPaymentCount} payment{unlinkedPaymentCount !== 1 ? "s aren't" : " isn't"} linked to a job, so no member pay is credited from {unlinkedPaymentCount !== 1 ? "them" : "it"}. Open the payment and pick its job to include it.</div>
              )}
              {paidJobsWithoutAssignments > 0 && (
                <div style={{ marginTop: unlinkedPaymentCount > 0 ? 6 : 0 }}>{paidJobsWithoutAssignments} paid job{paidJobsWithoutAssignments !== 1 ? "s have" : " has"} nobody assigned, so {paidJobsWithoutAssignments !== 1 ? "they add" : "it adds"} no payout.</div>
              )}
            </div>
          )}
          {byMember.length === 0 && <div style={styles.emptyState}>No member payouts in this range. Payouts appear once a client payment is logged against a job that has members assigned.</div>}
          {byMember.map((m, i) => (
            <div key={i} style={styles.teamReportRow}>
              <div style={styles.teamReportHeader}>
                <span style={{ ...styles.teamDot, background: m.teamColor }} />
                <span style={styles.teamReportName}>{m.name}</span>
                <span style={styles.teamReportSubName}>{m.teamName}</span>
                <span style={styles.teamReportRevenue}>{fmtMoney(m.payout)}</span>
              </div>
              <div style={styles.barTrack}>
                <div style={{ ...styles.barFill, width: `${(m.payout / maxMemberPayout) * 100}%`, background: m.teamColor }} />
              </div>
              <div style={styles.teamReportMeta}>
                <span>{m.jobs} paid job{m.jobs !== 1 ? "s" : ""}</span>
                <span>{m.completed} completed</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {reportView === "customer" && (
        <div style={styles.panel}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={styles.panelTitle}>Revenue by client</div>
            <div style={styles.clientSubTabRow}>
              <button onClick={() => setClientTypeFilter("all")} style={{ ...styles.clientSubTab, ...(clientTypeFilter === "all" ? styles.clientSubTabActive : {}) }}>All</button>
              <button onClick={() => setClientTypeFilter("new")} style={{ ...styles.clientSubTab, ...(clientTypeFilter === "new" ? styles.clientSubTabActive : {}) }}>New</button>
              <button onClick={() => setClientTypeFilter("returning")} style={{ ...styles.clientSubTab, ...(clientTypeFilter === "returning" ? { ...styles.clientSubTabActive, background: "#3A7D44" } : {}) }}>Previous</button>
            </div>
          </div>
          {(() => {
            const filteredByType = byCustomer.filter(({ customer: c }) =>
              clientTypeFilter === "all" || (c.clientStatus || "new") === clientTypeFilter
            );
            const maxRev = Math.max(1, ...filteredByType.map(c => c.revenue));
            const totalRev = filteredByType.reduce((s, c) => s + c.revenue, 0);
            return (
              <>
                <div style={styles.panelSubNote}>
                  Sorted by total paid in range · {filteredByType.length} {clientTypeFilter === "returning" ? "previous" : clientTypeFilter === "new" ? "new" : "active"} client{filteredByType.length !== 1 ? "s" : ""} · total revenue <strong>{fmtMoney(totalRev)}</strong>
                </div>
                {filteredByType.length === 0 && <div style={styles.emptyState}>No {clientTypeFilter === "returning" ? "previous" : clientTypeFilter === "new" ? "new" : ""} client activity in this range.</div>}
                {filteredByType.map(({ customer, revenue, paymentCount, totalJobs, completed, scheduled, cancelled }) => (
                  <div key={customer.id} style={styles.teamReportRow}>
                    <div style={styles.teamReportHeader}>
                      <div style={{ flex: 1 }}>
                        <span style={styles.teamReportName}>{customer.name}</span>
                        <span style={{ ...styles.pill, marginLeft: 8, background: (customer.clientStatus || "new") === "new" ? "#E4F0FA" : "#E8F5E2", color: (customer.clientStatus || "new") === "new" ? "#1E6FA8" : "#3A7D44", fontSize: 10.5 }}>
                          {(customer.clientStatus || "new") === "new" ? "New" : "Previous"}
                        </span>
                        {customer.address && <span style={{ ...styles.teamReportSubName, display: "block", marginTop: 2 }}>{customer.address}</span>}
                      </div>
                      <span style={styles.teamReportRevenue}>{fmtMoney(revenue)}</span>
                    </div>
                    <div style={styles.barTrack}>
                      <div style={{ ...styles.barFill, width: `${(revenue / maxRev) * 100}%`, background: "#1E6FA8" }} />
                    </div>
                    <div style={styles.teamReportMeta}>
                      <span>{paymentCount} payment{paymentCount !== 1 ? "s" : ""}</span>
                      <span>{totalJobs} job{totalJobs !== 1 ? "s" : ""} total</span>
                      <span>{completed} completed</span>
                      <span>{scheduled} upcoming</span>
                      {cancelled > 0 && <span>{cancelled} cancelled</span>}
                    </div>
                  </div>
                ))}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ---------- History modal ----------
function HistoryModal({ customer, jobs, payments, teamMap, onClose }) {
  const sortedJobs = [...jobs].sort((a, b) => b.date.localeCompare(a.date));
  const sortedPayments = [...payments].sort((a, b) => b.date.localeCompare(a.date));
  return (
    <Modal title={`${customer.name} — history`} onClose={onClose} wide>
      <div style={styles.historySplit}>
        <div>
          <div style={styles.panelTitle}>Services performed</div>
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {sortedJobs.map(j => (
              <div key={j.id} style={styles.historyRow}>
                <span style={{ ...styles.teamDot, background: teamMap[j.teamId]?.color || "#ccc" }} />
                <div style={{ flex: 1 }}>
                  <div style={styles.historyMain}>{j.service}</div>
                  <div style={styles.historySub}>{fmtDate(j.date)} · {teamMap[j.teamId]?.name}</div>
                </div>
                <Pill bg={STATUS_STYLES[j.status].bg} color={STATUS_STYLES[j.status].color}>{STATUS_STYLES[j.status].label}</Pill>
              </div>
            ))}
            {sortedJobs.length === 0 && <div style={styles.emptyState}>No services on record.</div>}
          </div>
        </div>
        <div>
          <div style={styles.panelTitle}>Payments</div>
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {sortedPayments.map(p => (
              <div key={p.id} style={styles.historyRow}>
                <div style={{ flex: 1 }}>
                  <div style={styles.historyMain}>{fmtMoney(Number(p.amount))}</div>
                  <div style={styles.historySub}>{fmtDate(p.date)} · {p.method}{p.notes ? ` · ${p.notes}` : ""}</div>
                </div>
              </div>
            ))}
            {sortedPayments.length === 0 && <div style={styles.emptyState}>No payments on record.</div>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---------- Form Modals ----------
function JobModal({ job, customers, teams, onSave, onDelete, onClose }) {
  const [form, setForm] = useState({ ...job, assignments: job.assignments || [], recurrence: job.recurrence || { frequency: "none" } });
  const [pendingAction, setPendingAction] = useState(null); // "save" | "delete" | null — set when we need to ask about scope
  const selectedTeam = teams.find(t => t.id === form.teamId);
  const teamMembers = [...(selectedTeam?.members || [])].sort(alphabetical);
  const isPartOfSeries = !!form.seriesId;

  function isAssigned(member) {
    return form.assignments.some(a => a.member === member);
  }
  function toggleMember(member) {
    if (isAssigned(member)) {
      setForm({ ...form, assignments: form.assignments.filter(a => a.member !== member) });
    } else {
      setForm({ ...form, assignments: [...form.assignments, { member, amount: "" }] });
    }
  }
  function setAmount(member, amount) {
    setForm({ ...form, assignments: form.assignments.map(a => a.member === member ? { ...a, amount } : a) });
  }
  function handleTeamChange(teamId) {
    const newTeam = teams.find(t => t.id === teamId);
    const validMembers = newTeam?.members || [];
    setForm({ ...form, teamId, assignments: form.assignments.filter(a => validMembers.includes(a.member)) });
  }

  const totalPayout = form.assignments.reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const cleanForm = { ...form, assignments: form.assignments.map(a => ({ ...a, amount: Number(a.amount) || 0 })) };

  function requestSave() {
    if (job.id && isPartOfSeries) {
      setPendingAction("save");
    } else {
      onSave(cleanForm);
    }
  }
  function requestDelete() {
    if (isPartOfSeries) {
      setPendingAction("delete");
    } else {
      onDelete(job.id);
    }
  }

  if (pendingAction) {
    return (
      <Modal title={pendingAction === "save" ? "Apply changes to..." : "Delete which occurrences?"} onClose={() => setPendingAction(null)}>
        <p style={styles.scopePrompt}>
          This job is part of a recurring series. {pendingAction === "save" ? "Should these changes apply to" : "Should the delete apply to"} just this date, or this and every future occurrence?
        </p>
        <div style={styles.scopeBtnCol}>
          <button
            style={styles.scopeOptionBtn}
            onClick={() => pendingAction === "save" ? onSave(cleanForm, "this") : onDelete(job.id, "this")}
          >
            Just this occurrence
            <span style={styles.scopeOptionSub}>{fmtDate(form.date)} only — leaves the series and the rest untouched.</span>
          </button>
          <button
            style={styles.scopeOptionBtn}
            onClick={() => pendingAction === "save" ? onSave(cleanForm, "future") : onDelete(job.id, "future")}
          >
            This and all future occurrences
            <span style={styles.scopeOptionSub}>Updates the recurring series from {fmtDate(form.date)} onward.</span>
          </button>
        </div>
        <div style={styles.modalActions}>
          <div style={{ flex: 1 }} />
          <button style={styles.secondaryBtnSm} onClick={() => setPendingAction(null)}>Back</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={job.id ? "Edit job" : "New job"} onClose={onClose}>
      <Field label="Client">
        <select style={styles.input} value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value })}>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Team">
        <select style={styles.input} value={form.teamId} onChange={e => handleTeamChange(e.target.value)}>
          {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </Field>
      <div style={{ display: "flex", gap: 12 }}>
        <Field label="Date"><input type="date" style={styles.input} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
        <Field label="Time"><input type="time" style={styles.input} value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} /></Field>
      </div>
      <Field label="Service type">
        <select style={styles.input} value={form.service} onChange={e => setForm({ ...form, service: e.target.value })}>
          {SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <Field label="Status">
        <select style={styles.input} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
          {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{STATUS_STYLES[s].label}</option>)}
        </select>
      </Field>

      <Field label="Repeats">
        <select
          style={styles.input}
          value={form.recurrence?.frequency || "none"}
          onChange={e => setForm({ ...form, recurrence: { frequency: e.target.value } })}
        >
          {RECURRENCE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {form.recurrence?.frequency !== "none" && !job.id && (
          <div style={styles.recurrenceNote}>Future occurrences will be created automatically and kept several months ahead.</div>
        )}
        {isPartOfSeries && (
          <div style={styles.recurrenceNote}>This job belongs to a recurring series. Saving or deleting will ask whether to apply the change to just this date or to it and future occurrences too.</div>
        )}
      </Field>

      <Field label="Who worked this job, and how much do they get paid?">
        {teamMembers.length === 0 && <div style={styles.emptyState}>This team has no members yet. Add members in the Teams tab first.</div>}
        <div style={styles.assignmentList}>
          {teamMembers.map(member => {
            const assignment = form.assignments.find(a => a.member === member);
            const active = !!assignment;
            return (
              <div key={member} style={{ ...styles.assignmentRow, ...(active ? styles.assignmentRowActive : {}) }}>
                <label style={styles.assignmentCheckLabel}>
                  <input type="checkbox" checked={active} onChange={() => toggleMember(member)} style={styles.assignmentCheckbox} />
                  <span>{member}</span>
                </label>
                {active && (
                  <div style={styles.assignmentAmountWrap}>
                    <span style={styles.assignmentDollar}>$</span>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      style={styles.assignmentAmountInput}
                      value={assignment.amount}
                      onChange={e => setAmount(member, e.target.value)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {form.assignments.length > 0 && (
          <div style={styles.assignmentTotal}>Total payout for this job: <strong>{fmtMoney(totalPayout)}</strong></div>
        )}
      </Field>

      <Field label="Notes">
        <textarea style={{ ...styles.input, minHeight: 60 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <div style={styles.modalActions}>
        {job.id && <button style={styles.dangerBtn} onClick={requestDelete}><Trash2 size={15} /> Delete</button>}
        <div style={{ flex: 1 }} />
        <button style={styles.secondaryBtnSm} onClick={onClose}>Cancel</button>
        <button style={styles.primaryBtn} onClick={requestSave}>Save job</button>
      </div>
    </Modal>
  );
}

function TeamModal({ team, jobCount, onSave, onDelete, onClose }) {
  const [form, setForm] = useState(team);
  const [newMember, setNewMember] = useState("");

  function addMember() {
    const name = newMember.trim();
    if (!name) return;
    setForm({ ...form, members: [...form.members, name] });
    setNewMember("");
  }
  function removeMember(idx) {
    setForm({ ...form, members: form.members.filter((_, i) => i !== idx) });
  }

  return (
    <Modal title={team.id ? "Edit team" : "New team"} onClose={onClose}>
      <Field label="Team name"><input style={styles.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>

      <Field label="Color">
        <div style={styles.colorSwatchRow}>
          {TEAM_COLORS.map(c => (
            <button
              key={c}
              onClick={() => setForm({ ...form, color: c })}
              style={{ ...styles.colorSwatchBtn, background: c, ...(form.color === c ? styles.colorSwatchBtnActive : {}) }}
              aria-label={`Choose color ${c}`}
            />
          ))}
        </div>
      </Field>

      <Field label="Members">
        <div style={styles.memberInputRow}>
          <input
            style={{ ...styles.input, flex: 1 }}
            placeholder="Member name"
            value={newMember}
            onChange={e => setNewMember(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addMember(); } }}
          />
          <button style={styles.addMemberBtn} onClick={addMember}><UserPlus size={16} /></button>
        </div>
        <div style={styles.teamMemberList}>
          {form.members.length === 0 && <span style={styles.teamMemberEmpty}>No members yet — add one above.</span>}
          {/* Shown A–Z, but each chip keeps its real index so removal targets the right name. */}
          {form.members
            .map((m, i) => ({ name: m, idx: i }))
            .sort((a, b) => alphabetical(a.name, b.name))
            .map(({ name, idx }) => (
              <span key={idx} style={styles.teamMemberChipEditable}>
                {name}
                <button style={styles.removeMemberBtn} onClick={() => removeMember(idx)}><X size={12} /></button>
              </span>
            ))}
        </div>
      </Field>

      {team.id && jobCount > 0 && (
        <div style={styles.teamWarning}>This team has {jobCount} job{jobCount !== 1 ? "s" : ""} assigned. Deleting it will leave those jobs unassigned.</div>
      )}

      <div style={styles.modalActions}>
        {team.id && <button style={styles.dangerBtn} onClick={() => onDelete(team.id)}><Trash2 size={15} /> Delete</button>}
        <div style={{ flex: 1 }} />
        <button style={styles.secondaryBtnSm} onClick={onClose}>Cancel</button>
        <button style={styles.primaryBtn} onClick={() => onSave(form)} disabled={!form.name}>Save team</button>
      </div>
    </Modal>
  );
}

function MemberModal({ member, onSave, onDelete, onClose }) {
  const [form, setForm] = useState(member);
  return (
    <Modal title={member.id ? "Edit member" : "New member"} onClose={onClose}>
      <Field label="Full name"><input style={styles.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
      <Field label="Phone"><input style={styles.input} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
      <Field label="Address"><input style={styles.input} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></Field>
      <Field label="Hire date"><input type="date" style={styles.input} value={form.hireDate || ""} onChange={e => setForm({ ...form, hireDate: e.target.value })} /></Field>
      <Field label="Notes">
        <textarea style={{ ...styles.input, minHeight: 60 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
      </Field>
      {member.id && (
        <div style={styles.recurrenceNote}>To put this person on a crew, head to the Teams tab and add their name there — renaming them here will update it everywhere automatically.</div>
      )}
      <div style={styles.modalActions}>
        {member.id && <button style={styles.dangerBtn} onClick={() => onDelete(member.id)}><Trash2 size={15} /> Delete</button>}
        <div style={{ flex: 1 }} />
        <button style={styles.secondaryBtnSm} onClick={onClose}>Cancel</button>
        <button style={styles.primaryBtn} onClick={() => onSave(form)} disabled={!form.name}>Save member</button>
      </div>
    </Modal>
  );
}

function CustomerModal({ customer, onSave, onDelete, onClose }) {
  const [form, setForm] = useState({ clientStatus: "new", ...customer });
  return (
    <Modal title={customer.id ? "Edit client" : "New client"} onClose={onClose}>
      <Field label="Full name"><input style={styles.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
      <Field label="Phone"><input style={styles.input} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
      <Field label="Email"><input style={styles.input} value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
      <Field label="Address"><input style={styles.input} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></Field>
      <Field label="Client type">
        <div style={styles.clientTypeToggleRow}>
          <button
            style={{ ...styles.clientTypeBtn, ...(form.clientStatus === "new" || !form.clientStatus ? styles.clientTypeBtnActive : {}) }}
            onClick={() => setForm({ ...form, clientStatus: "new" })}
          >New client</button>
          <button
            style={{ ...styles.clientTypeBtn, ...(form.clientStatus === "returning" ? { ...styles.clientTypeBtnActive, background: "#3A7D44", borderColor: "#3A7D44" } : {}) }}
            onClick={() => setForm({ ...form, clientStatus: "returning" })}
          >Previous client</button>
        </div>
      </Field>
      <Field label="Notes (access codes, pets, preferences)">
        <textarea style={{ ...styles.input, minHeight: 60 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <div style={styles.modalActions}>
        {customer.id && <button style={styles.dangerBtn} onClick={() => onDelete(customer.id)}><Trash2 size={15} /> Delete</button>}
        <div style={{ flex: 1 }} />
        <button style={styles.secondaryBtnSm} onClick={onClose}>Cancel</button>
        <button style={styles.primaryBtn} onClick={() => onSave(form)} disabled={!form.name}>Save client</button>
      </div>
    </Modal>
  );
}

function PaymentModal({ payment, customers, jobs, onSave, onDelete, onClose }) {
  const [form, setForm] = useState(payment);
  const custJobs = jobs.filter(j => j.customerId === form.customerId);
  return (
    <Modal title={payment.id ? "Edit payment" : "Log payment"} onClose={onClose}>
      <Field label="Client">
        <select style={styles.input} value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value, jobId: "" })}>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Related job (optional)">
        <select style={styles.input} value={form.jobId} onChange={e => setForm({ ...form, jobId: e.target.value })}>
          <option value="">— none —</option>
          {custJobs.map(j => <option key={j.id} value={j.id}>{fmtDate(j.date)} · {j.service}</option>)}
        </select>
      </Field>
      <div style={{ display: "flex", gap: 12 }}>
        <Field label="Date"><input type="date" style={styles.input} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
        <Field label="Amount ($)"><input type="number" step="0.01" style={styles.input} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></Field>
      </div>
      <Field label="Method">
        <select style={styles.input} value={form.method} onChange={e => setForm({ ...form, method: e.target.value })}>
          {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </Field>
      <Field label="Notes (optional)">
        <textarea style={{ ...styles.input, minHeight: 50 }} value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <div style={styles.modalActions}>
        {payment.id && <button style={styles.dangerBtn} onClick={() => onDelete(payment.id)}><Trash2 size={15} /> Delete</button>}
        <div style={{ flex: 1 }} />
        <button style={styles.secondaryBtnSm} onClick={onClose}>Cancel</button>
        <button style={styles.primaryBtn} onClick={() => onSave({ ...form, amount: Number(form.amount) || 0 })} disabled={!form.amount}>Save payment</button>
      </div>
    </Modal>
  );
}

function ExpenseModal({ expense, onSave, onDelete, onClose }) {
  const [form, setForm] = useState(expense);
  return (
    <Modal title={expense.id ? "Edit expense" : "Log expense"} onClose={onClose}>
      <div style={{ display: "flex", gap: 12 }}>
        <Field label="Date"><input type="date" style={styles.input} value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
        <Field label="Amount ($)"><input type="number" step="0.01" style={styles.input} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></Field>
      </div>
      <Field label="Category">
        <select style={styles.input} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
          {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Description"><input style={styles.input} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
      <div style={styles.modalActions}>
        {expense.id && <button style={styles.dangerBtn} onClick={() => onDelete(expense.id)}><Trash2 size={15} /> Delete</button>}
        <div style={{ flex: 1 }} />
        <button style={styles.secondaryBtnSm} onClick={onClose}>Cancel</button>
        <button style={styles.primaryBtn} onClick={() => onSave({ ...form, amount: Number(form.amount) || 0 })} disabled={!form.amount || !form.description}>Save expense</button>
      </div>
    </Modal>
  );
}

// ---------- Styles ----------
const globalCss = `
  * { box-sizing: border-box; }
  body { margin: 0; }
  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-thumb { background: #c7d4ce; border-radius: 4px; }
  button { font-family: inherit; cursor: pointer; }
  select, input, textarea { font-family: inherit; }
  @media (max-width: 760px) {
    .cs-app { flex-direction: column; }
  }
  @media print {
    body * { visibility: hidden !important; }
    #invoice-print, #invoice-print * { visibility: visible !important; }
    #invoice-print { position: absolute !important; left: 0; top: 0; width: 100%; max-width: none !important; box-shadow: none !important; border-radius: 0 !important; padding: 0 !important; }
    .no-print { display: none !important; }
  }
`;

const styles = {
  app: { display: "flex", minHeight: "100vh", background: "#EFF6FB", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: "#2B2B2B" },
  appMobile: { flexDirection: "column" },
  sidebar: { width: 220, background: "#1E6FA8", color: "#fff", padding: "24px 16px", display: "flex", flexDirection: "column", flexShrink: 0 },
  sidebarMobile: { width: "100%", padding: "12px 12px 14px", flexShrink: 0 },
  navMobile: { marginTop: 12, display: "flex", flexDirection: "row", flexWrap: "nowrap", overflowX: "auto", gap: 6, paddingBottom: 4, WebkitOverflowScrolling: "touch" },
  navBtnMobile: { width: "auto", flexShrink: 0, whiteSpace: "nowrap", marginBottom: 0, padding: "8px 12px" },
  signOutBtnMobile: { marginTop: 12, alignSelf: "flex-start", padding: "7px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)", background: "transparent", color: "#E6F2FB", fontSize: 13, fontWeight: 600 },
  brand: { display: "flex", alignItems: "center", gap: 10, padding: "0 8px" },
  brandMark: { width: 36, height: 36, borderRadius: 10, background: "#D3E8F7", color: "#1E6FA8", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, letterSpacing: 0.5 },
  brandName: { fontWeight: 700, fontSize: 14, letterSpacing: 0.1, lineHeight: 1.25 },
  brandSub: { fontSize: 11.5, color: "#9FC8E3", marginTop: 1 },
  navBtn: { display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 12px", borderRadius: 9, border: "none", background: "transparent", color: "#D3E8F7", fontSize: 14, fontWeight: 500, marginBottom: 4, textAlign: "left" },
  navBtnActive: { background: "rgba(255,255,255,0.14)", color: "#fff" },
  sidebarFooter: { marginTop: "auto", paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.12)" },
  teamLegendTitle: { fontSize: 11, color: "#9FC8E3", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10, fontWeight: 600 },
  teamLegendRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 13 },
  teamLegendName: { color: "#E6F2FB" },
  teamDot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block", flexShrink: 0 },
  errorBanner: { position: "absolute", top: 0, left: 0, right: 0, zIndex: 50, background: "#F4E3DD", color: "#9A3B22", padding: "10px 16px", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  errorDismiss: { background: "transparent", border: "none", color: "#9A3B22", cursor: "pointer", fontSize: 14, fontWeight: 700, flexShrink: 0 },
  accountBox: { marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.12)" },
  accountEmail: { fontSize: 12, color: "#9FC8E3", marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  signOutBtn: { width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.18)", background: "transparent", color: "#E6F2FB", fontSize: 13, cursor: "pointer", fontWeight: 600 },

  main: { flex: 1, padding: "32px 40px", overflowY: "auto", minWidth: 0 },
  mainMobile: { padding: "0 14px 18px" },
  topBar: { position: "sticky", top: 0, zIndex: 20, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, margin: "-32px -40px 24px", padding: "12px 40px", background: "rgba(239,246,251,0.92)", backdropFilter: "blur(6px)", borderBottom: "1px solid #DCE7F0" },
  topBarMobile: { margin: "0 -14px 16px", padding: "10px 14px" },
  topBarEmail: { fontSize: 12.5, color: "#637D76", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  topBarSignOut: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0, background: "#fff", border: "1.5px solid #CFE2F0", color: "#1E6FA8", borderRadius: 8, padding: "7px 14px", fontWeight: 600, fontSize: 13 },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22, flexWrap: "wrap", gap: 12 },
  pageTitle: { fontSize: 24, fontWeight: 700, margin: 0, letterSpacing: -0.3 },
  pageSub: { fontSize: 14, color: "#637D76", margin: "4px 0 0" },

  primaryBtn: { display: "flex", alignItems: "center", gap: 6, background: "#1E6FA8", color: "#fff", border: "none", borderRadius: 9, padding: "10px 16px", fontWeight: 600, fontSize: 13.5 },
  secondaryBtn: { width: "100%", marginTop: 14, background: "#fff", border: "1.5px solid #CFE2F0", color: "#1E6FA8", borderRadius: 8, padding: "9px 0", fontWeight: 600, fontSize: 13 },
  secondaryBtnSm: { background: "#fff", border: "1.5px solid #CFE2F0", color: "#4B5A55", borderRadius: 8, padding: "9px 16px", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6 },
  secondaryBtnSmActive: { background: "#1E6FA8", color: "#fff", borderColor: "#1E6FA8" },
  dangerBtn: { display: "flex", alignItems: "center", gap: 6, background: "#FBEAE5", color: "#C9694F", border: "none", borderRadius: 8, padding: "9px 14px", fontWeight: 600, fontSize: 13 },
  linkBtn: { display: "flex", alignItems: "center", gap: 5, background: "transparent", border: "none", color: "#1E6FA8", fontWeight: 600, fontSize: 12.5, padding: "4px 8px 4px 0" },
  iconBtn: { background: "transparent", border: "none", color: "#637D76", padding: 6, borderRadius: 6, display: "flex" },

  filterRow: { display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap", alignItems: "center" },
  filterRowLabel: { display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: "#8A9590", marginRight: 4 },
  dateInput: { border: "1.5px solid #CFE2F0", borderRadius: 8, padding: "7px 10px", fontSize: 13, color: "#2B2B2B", background: "#fff" },

  selectWrap: { position: "relative", display: "inline-flex", alignItems: "center" },
  // Left pad clears the colour dot, right pad clears the chevron.
  selectInput: {
    appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
    border: "1.5px solid #CFE2F0", borderRadius: 8, padding: "7px 30px 7px 26px",
    fontSize: 13, fontWeight: 600, color: "#2B2B2B", background: "#fff",
    cursor: "pointer", minWidth: 170,
  },
  selectInputNoDot: { paddingLeft: 12 },
  selectInputActive: { borderColor: "#1E6FA8", color: "#1E6FA8" },
  selectDot: { position: "absolute", left: 11, pointerEvents: "none" },
  selectChevron: { position: "absolute", right: 9, color: "#8A9590", pointerEvents: "none" },
  dateRangeDivider: { width: 1, height: 22, background: "#CFE2F0", margin: "0 2px" },
  filterChip: { display: "flex", alignItems: "center", gap: 6, background: "#fff", border: "1.5px solid #CFE2F0", borderRadius: 20, padding: "7px 14px", fontSize: 13, fontWeight: 600, color: "#4B5A55" },
  filterChipActive: { background: "#1E6FA8", color: "#fff", borderColor: "#1E6FA8" },

  jobList: { display: "flex", flexDirection: "column", gap: 12 },
  quoteCard: { display: "flex", background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 2px rgba(20,40,35,0.06)" },
  quoteStatsRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 20 },
  quoteStatCard: { background: "#fff", borderRadius: 12, padding: "14px 16px", border: "1.5px solid #E0EAF2", textAlign: "center" },
  quoteStatNum: { fontSize: 24, fontWeight: 700 },
  quoteStatLabel: { fontSize: 11.5, fontWeight: 600, marginTop: 2 },
  dayGroupList: { display: "flex", flexDirection: "column", gap: 26 },
  dayGroup: {},
  dayGroupHeader: { display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12, paddingBottom: 8, borderBottom: "1.5px solid #D5E5F2" },
  dayGroupTitle: { fontSize: 15.5, fontWeight: 700, color: "#2B2B2B" },
  dayGroupTitleToday: { color: "#1E6FA8" },
  dayGroupSub: { fontSize: 12.5, color: "#8A9590" },
  dayGroupCount: { marginLeft: "auto", fontSize: 11.5, fontWeight: 700, color: "#637D76", background: "#E4F0FA", padding: "3px 9px", borderRadius: 20 },

  driveConnector: { display: "flex", alignItems: "center", gap: 10, padding: "2px 0 2px 22px" },
  driveConnectorLine: { width: 2, height: 16, opacity: 0.4, borderRadius: 1, flexShrink: 0 },
  driveConnectorLink: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#1E6FA8", textDecoration: "none", background: "#fff", border: "1.5px solid #CFE2F0", borderRadius: 20, padding: "5px 12px" },

  selectedDayBanner: { display: "flex", alignItems: "center", gap: 8, background: "#E4F0FA", color: "#1E6FA8", padding: "10px 14px", borderRadius: 10, fontSize: 13.5, marginBottom: 16 },
  clearDayBtn: { display: "flex", alignItems: "center", gap: 4, marginLeft: "auto", background: "#fff", border: "1.5px solid #C2DCEF", color: "#1E6FA8", borderRadius: 7, padding: "4px 10px", fontWeight: 600, fontSize: 12 },

  calendarCard: { background: "#fff", borderRadius: 14, padding: 18, marginBottom: 18, boxShadow: "0 1px 2px rgba(20,40,35,0.06)", maxWidth: 360 },
  calendarHeader: { display: "flex", alignItems: "center", gap: 8, marginBottom: 14 },
  calendarNavBtn: { background: "#EFF6FB", border: "none", borderRadius: 7, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: "#1E6FA8" },
  calendarMonthLabel: { fontWeight: 700, fontSize: 14.5, flex: 1, textAlign: "center" },
  calendarTodayBtn: { background: "transparent", border: "1.5px solid #CFE2F0", borderRadius: 7, padding: "4px 10px", fontSize: 11.5, fontWeight: 700, color: "#4B5A55" },
  calendarWeekRow: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 4 },
  calendarWeekday: { textAlign: "center", fontSize: 11, fontWeight: 700, color: "#9AA9A3", padding: "4px 0" },
  calendarGrid: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 },
  calendarEmptyCell: { aspectRatio: "1" },
  calendarDayCell: { aspectRatio: "1", border: "none", background: "transparent", borderRadius: 9, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, fontSize: 13, fontWeight: 600, color: "#2B2B2B" },
  calendarDayCellToday: { background: "#EFF6FB", color: "#1E6FA8", fontWeight: 700 },
  calendarDayCellSelected: { background: "#1E6FA8", color: "#fff" },
  calendarDotRow: { display: "flex", gap: 2 },
  calendarDot: { width: 4.5, height: 4.5, borderRadius: "50%", display: "inline-block" },
  jobCard: { display: "flex", background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 2px rgba(20,40,35,0.06)" },
  jobTeamBar: { width: 5, flexShrink: 0 },
  jobCardBody: { padding: "14px 18px", flex: 1 },
  jobCardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  jobDate: { fontSize: 12.5, color: "#637D76", fontWeight: 600 },
  jobCustomer: { fontSize: 16, fontWeight: 700, marginTop: 2, display: "flex", alignItems: "center", gap: 6 },
  jobRepeatIcon: { color: "#8A9590" },
  jobMeta: { display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12.5, color: "#637D76", marginTop: 8, alignItems: "center" },
  jobNoteRow: { display: "flex", alignItems: "flex-start", gap: 6, marginTop: 8, fontSize: 12.5, color: "#A6772E", background: "#FBF0DD", padding: "6px 10px", borderRadius: 8, lineHeight: 1.4 },
  jobNoteIcon: { flexShrink: 0, marginTop: 1 },
  jobActions: { display: "flex", gap: 4, marginTop: 10, borderTop: "1px solid #F0F3F1", paddingTop: 8 },
  jobAssignRow: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 },
  jobAssignChip: { fontSize: 11.5, fontWeight: 600, color: "#4B5A55", background: "#EFF6FB", padding: "3px 9px", borderRadius: 20 },

  pill: { fontSize: 11.5, fontWeight: 700, padding: "4px 10px", borderRadius: 20, whiteSpace: "nowrap" },

  customerGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 16 },
  customerCard: { background: "#fff", borderRadius: 14, padding: 18, boxShadow: "0 1px 2px rgba(20,40,35,0.06)" },
  customerCardHeader: { display: "flex", alignItems: "center", gap: 12 },
  avatarCircle: { width: 42, height: 42, borderRadius: "50%", background: "#E4F0FA", color: "#1E6FA8", fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  customerName: { fontWeight: 700, fontSize: 15.5 },
  customerPhone: { fontSize: 12.5, color: "#637D76", display: "flex", alignItems: "center", gap: 5, marginTop: 2 },
  customerAddress: { fontSize: 13, color: "#4B5A55", marginTop: 12, display: "flex", alignItems: "flex-start", gap: 6 },
  memberHireDate: { fontSize: 12, color: "#8A9590", marginTop: 6 },
  customerNotes: { fontSize: 12.5, color: "#8A9590", marginTop: 8, fontStyle: "italic", lineHeight: 1.4 },

  clientSubTabRow: { display: "flex", gap: 0, marginBottom: 22, background: "#fff", borderRadius: 12, padding: 5, boxShadow: "0 1px 2px rgba(20,40,35,0.06)", width: "fit-content" },
  clientSubTab: { background: "transparent", border: "none", borderRadius: 9, padding: "9px 20px", fontSize: 13.5, fontWeight: 600, color: "#637D76", display: "flex", alignItems: "center", gap: 7 },
  clientSubTabActive: { background: "#1E6FA8", color: "#fff" },
  clientSubTabCount: { fontSize: 11, fontWeight: 700, background: "rgba(255,255,255,0.25)", padding: "2px 7px", borderRadius: 20 },

  clientTypeToggleRow: { display: "flex", gap: 8 },
  clientTypeBtn: { flex: 1, padding: "9px 0", border: "1.5px solid #D5E5F2", borderRadius: 8, background: "#fff", fontSize: 13, fontWeight: 600, color: "#4B5A55" },
  clientTypeBtnActive: { background: "#1E6FA8", borderColor: "#1E6FA8", color: "#fff" },
  customerStatsRow: { display: "flex", flexWrap: "wrap", gap: 24, marginTop: 16, paddingTop: 14, borderTop: "1px solid #F0F3F1" },
  customerStatNum: { fontWeight: 700, fontSize: 16 },
  customerStatLabel: { fontSize: 11.5, color: "#8A9590", marginTop: 1 },

  teamColorSwatch: { width: 42, height: 42, borderRadius: 12, flexShrink: 0 },
  teamMemberList: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 },
  teamMemberEmpty: { fontSize: 12.5, color: "#8A9590", fontStyle: "italic" },
  teamMemberChip: { fontSize: 12, fontWeight: 600, color: "#1E6FA8", background: "#E4F0FA", padding: "4px 10px", borderRadius: 20 },
  teamMemberChipEditable: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#1E6FA8", background: "#E4F0FA", padding: "5px 6px 5px 11px", borderRadius: 20 },
  removeMemberBtn: { background: "rgba(26,77,74,0.12)", border: "none", borderRadius: "50%", width: 17, height: 17, display: "flex", alignItems: "center", justifyContent: "center", color: "#1E6FA8", padding: 0 },
  colorSwatchRow: { display: "flex", gap: 8 },
  colorSwatchBtn: { width: 30, height: 30, borderRadius: 9, border: "2px solid transparent", padding: 0 },
  colorSwatchBtnActive: { border: "2px solid #2B2B2B", transform: "scale(1.08)" },
  memberInputRow: { display: "flex", gap: 8 },
  addMemberBtn: { background: "#1E6FA8", color: "#fff", border: "none", borderRadius: 8, padding: "0 14px", display: "flex", alignItems: "center", justifyContent: "center" },
  teamWarning: { fontSize: 12.5, color: "#A6772E", background: "#FBF0DD", padding: "10px 12px", borderRadius: 8, marginTop: -4, marginBottom: 14 },

  tableWrap: { background: "#fff", borderRadius: 14, overflowX: "auto", boxShadow: "0 1px 2px rgba(20,40,35,0.06)" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.5, color: "#8A9590", padding: "14px 18px", borderBottom: "1px solid #F0F3F1", fontWeight: 700 },
  tr: { cursor: "pointer", transition: "background 0.1s" },
  td: { padding: "13px 18px", fontSize: 13.5, borderBottom: "1px solid #F5F7F6" },
  methodTag: { display: "inline-flex", alignItems: "center", gap: 6, color: "#4B5A55" },

  printOverlay: { position: "fixed", inset: 0, background: "rgba(20,40,35,0.45)", display: "flex", flexDirection: "column", alignItems: "center", overflowY: "auto", padding: "24px 16px", zIndex: 50 },
  printToolbar: { display: "flex", gap: 10, marginBottom: 16, width: "100%", maxWidth: 720, justifyContent: "flex-end" },
  invoiceSheet: { background: "#fff", width: "100%", maxWidth: 720, borderRadius: 8, padding: "40px 44px", boxShadow: "0 8px 30px rgba(20,40,35,0.25)", color: "#23332e" },
  invTh: { textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: "#6b7975", padding: "8px 6px", fontWeight: 700 },
  invTd: { padding: "9px 6px", fontSize: 13.5 },

  statsRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 22 },
  statCard: { background: "#fff", borderRadius: 14, padding: 18, display: "flex", gap: 14, alignItems: "center", boxShadow: "0 1px 2px rgba(20,40,35,0.06)" },
  statIconWrap: { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statLabel: { fontSize: 12, color: "#8A9590", fontWeight: 600 },
  statValue: { fontSize: 20, fontWeight: 700, marginTop: 1 },
  statSub: { fontSize: 11.5, color: "#8A9590" },

  reportGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 },
  panel: { background: "#fff", borderRadius: 14, padding: 20, boxShadow: "0 1px 2px rgba(20,40,35,0.06)" },
  panelTitle: { fontWeight: 700, fontSize: 14.5, marginBottom: 14 },
  panelSubNote: { fontSize: 12.5, color: "#637D76", marginTop: -8, marginBottom: 16 },
  barLabelRow: { display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5, fontWeight: 600 },
  barTrack: { height: 8, background: "#EFF3F1", borderRadius: 6, overflow: "hidden" },
  barFill: { height: "100%", background: "#1E6FA8", borderRadius: 6 },
  expenseRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #F5F7F6", cursor: "pointer" },
  expenseDesc: { fontSize: 13.5, fontWeight: 600 },
  expenseMeta: { fontSize: 11.5, color: "#8A9590", marginTop: 2 },
  expenseAmount: { fontWeight: 700, color: "#C9694F", fontSize: 13.5 },

  expenseDayGroup: { marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #F0F3F1" },
  expenseDayHeader: { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 },
  expenseDayDate: { fontSize: 13, fontWeight: 700, color: "#22332E" },
  expenseDayTotal: { fontSize: 13, fontWeight: 700, color: "#C9694F", fontVariantNumeric: "tabular-nums" },

  plToggle: { display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#637D76", cursor: "pointer" },
  plRow: { display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "6px 0", fontSize: 14, fontWeight: 700, color: "#22332E" },
  plLabel: { fontWeight: 700 },
  plValue: { fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  plSubRow: { display: "flex", justifyContent: "space-between", padding: "3px 0 3px 14px", fontSize: 12.5, color: "#8A9590", fontVariantNumeric: "tabular-nums" },
  plMuted: { fontWeight: 500, color: "#9AA9A3", fontSize: 12 },
  plTotalRow: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 14, paddingTop: 12, borderTop: "2px solid #E7EDEA" },
  plTotalLabel: { fontSize: 15, fontWeight: 700, color: "#22332E" },
  plTotalValue: { fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums" },

  teamReportRow: { marginBottom: 18, paddingBottom: 16, borderBottom: "1px solid #F0F3F1" },
  teamReportHeader: { display: "flex", alignItems: "center", gap: 8, marginBottom: 6 },
  teamReportName: { fontWeight: 700, fontSize: 14 },
  teamReportSubName: { fontSize: 12, color: "#8A9590", fontWeight: 500 },
  teamReportRevenue: { marginLeft: "auto", fontWeight: 700, fontSize: 14, color: "#1E6FA8" },
  teamReportMeta: { display: "flex", gap: 14, fontSize: 12, color: "#8A9590", marginTop: 8 },

  emptyState: { padding: "30px 0", textAlign: "center", color: "#8A9590", fontSize: 13.5 },

  modalOverlay: { position: "fixed", inset: 0, background: "rgba(20,30,28,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 },
  modalCard: { background: "#fff", borderRadius: 16, width: "100%", maxHeight: "90vh", overflowY: "auto" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px 0" },
  modalTitle: { fontSize: 17, fontWeight: 700, margin: 0 },
  modalActions: { display: "flex", gap: 8, marginTop: 18, alignItems: "center" },

  label: { display: "block", fontSize: 12.5, fontWeight: 600, color: "#4B5A55", marginBottom: 6 },
  input: { width: "100%", padding: "9px 12px", border: "1.5px solid #D5E5F2", borderRadius: 8, fontSize: 13.5, color: "#2B2B2B", background: "#FAFAF8" },

  assignmentList: { display: "flex", flexDirection: "column", gap: 6 },
  assignmentRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #DCE9F2", background: "#FAFAF8" },
  assignmentRowActive: { borderColor: "#1E6FA8", background: "#EBF4FB" },
  assignmentCheckLabel: { display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 600, color: "#2B2B2B", cursor: "pointer" },
  assignmentCheckbox: { width: 15, height: 15, accentColor: "#1E6FA8" },
  assignmentAmountWrap: { display: "flex", alignItems: "center", gap: 4, background: "#fff", border: "1.5px solid #D5E5F2", borderRadius: 7, padding: "4px 8px" },
  assignmentDollar: { fontSize: 12.5, color: "#8A9590", fontWeight: 600 },
  assignmentAmountInput: { width: 64, border: "none", outline: "none", fontSize: 13, background: "transparent", textAlign: "right" },
  assignmentTotal: { fontSize: 13, color: "#4B5A55", marginTop: 10, textAlign: "right" },

  recurrenceNote: { fontSize: 12, color: "#8A9590", marginTop: 6, lineHeight: 1.4 },
  scopePrompt: { fontSize: 13.5, color: "#4B5A55", lineHeight: 1.5, marginTop: 0, marginBottom: 18 },
  scopeBtnCol: { display: "flex", flexDirection: "column", gap: 10 },
  scopeOptionBtn: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3, textAlign: "left", background: "#FAFAF8", border: "1.5px solid #D5E5F2", borderRadius: 10, padding: "12px 14px", fontSize: 14, fontWeight: 700, color: "#1E6FA8" },
  scopeOptionSub: { fontSize: 12, fontWeight: 500, color: "#8A9590" },

  historySplit: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 },
  historyRow: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F5F7F6" },
  historyMain: { fontSize: 13.5, fontWeight: 600 },
  historySub: { fontSize: 11.5, color: "#8A9590", marginTop: 2 },
};
